import React, { useState, useEffect, useMemo, useRef, useCallback, memo } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { Card, Spinner, Badge, Button, ButtonGroup, Modal, Form, Row, Col, InputGroup } from "react-bootstrap"
import Layout from "../components/layout/Layout"
import useAuthAxios from "../hooks/useAuthAxios"
import { useAuth } from "../context/AuthContext"
import { useCalendar } from "../context/CalendarContext"
import { formatDateForDisplay, formatDateForApi } from "../utils/dateUtils"
import { toast } from "react-toastify"
import BackButton from "../components/common/BackButton"
import DateTimePicker from "../components/common/DateTimePicker"
import FileUploadModal from "../components/inspections/FileUploadModal"
import jsPDF from "jspdf"
import html2canvas from "html2canvas"
import { useEExpert } from "../context/EExpertContext"
import DataTable from "../components/common/DataTable"
import RobotoFont from "../assets/fonts/Roboto.ttf"
import JSZip from "jszip"

// Move the modal component outside and memoize it
const PrintInspectionModal = memo(({ show, onHide, isPrinting, onPrint, printProgress }) => {
  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>Εκτύπωση Ελέγχου</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isPrinting ? (
          <div className="text-center py-4">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Φόρτωση...</span>
            </Spinner>
            <p className="mt-3 mb-0">{printProgress}</p>
          </div>
        ) : (
          <p>Θέλετε να συμπεριλάβετε φωτογραφίες στην εκτύπωση;</p>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={isPrinting}>
          Ακύρωση
        </Button>
        <Button onClick={() => onPrint(false)} disabled={isPrinting}>
          Χωρίς φωτογραφίες
        </Button>
        <Button onClick={() => onPrint(true)} disabled={isPrinting}>
          <i className="fas fa-images me-2" />
          Με φωτογραφίες
        </Button>
      </Modal.Footer>
    </Modal>
  )
})

const InspectionDetails = ({ dynamic = false }) => {
  const { inspectionId: urlInspectionId } = useParams()
  const navigate = useNavigate()
  const { get, put, delete: deleteFile, post } = useAuthAxios()
  const { getAppLookupValue, appLookups, isAdmin, user } = useAuth()
  const { refresh: refreshCalendar } = useCalendar()
  const { sessionId, setSessionId, setShowEExpert, showEExpert } = useEExpert()
  const [inspectionId, setInspectionId] = useState(urlInspectionId)
  const [inspection, setInspection] = useState(null)
  const [loading, setLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState({ status: false, action: null, progress: null })
  const [isModalSubmitting, setIsModalSubmitting] = useState(false)
  const [showRejectionModal, setShowRejectionModal] = useState(false)
  const [showCancelAssignmentModal, setShowCancelAssignmentModal] = useState(false)
  const [showAssignmentModal, setShowAssignmentModal] = useState({ show: false, type: null })
  const [rejectionComments, setRejectionComments] = useState("")
  const [cancelAssignmentComments, setCancelAssignmentComments] = useState("")
  const [selectedSubStatus, setSelectedSubStatus] = useState("")
  const [selectedExpertId, setSelectedExpertId] = useState("")
  const [assignmentDateTime, setAssignmentDateTime] = useState("")
  const [users, setUsers] = useState([])
  const [showDeleteFileModal, setShowDeleteFileModal] = useState(false)
  const [fileToDelete, setFileToDelete] = useState(null)
  const [showPreviewModal, setShowPreviewModal] = useState(false)
  const [previewFile, setPreviewFile] = useState(null)
  const [previewUrl, setPreviewUrl] = useState(null)
  const [showFileUpload, setShowFileUpload] = useState(false)
  const [downloadingFileId, setDownloadingFileId] = useState(null)
  const [showNotificationModal, setShowNotificationModal] = useState(false)
  const [notificationPrefs, setNotificationPrefs] = useState({
    sms: false,
    email: false
  })
  const [notificationPhone, setNotificationPhone] = useState("")
  const [notificationEmail, setNotificationEmail] = useState("")
  const [showPrintModal, setShowPrintModal] = useState(false)
  const [isPrinting, setIsPrinting] = useState(false)
  const [printProgress, setPrintProgress] = useState("")

  // Add at the top of the component with other refs/state
  const carContainerRef = useRef(null)
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 })

  // Add these new state declarations
  const [searchTerm, setSearchTerm] = useState("")
  const [isSearching, setIsSearching] = useState(false)
  const [searchResults, setSearchResults] = useState([])
  const [showSearchResultsModal, setShowSearchResultsModal] = useState(false)
  const [previewIndex, setPreviewIndex] = useState(0)

  // Add this after the other state declarations
  const [fileCache, setFileCache] = useState({})
  const [zoomPosition, setZoomPosition] = useState({ x: 0, y: 0 })
  const [zoomLevel, setZoomLevel] = useState(1)
  const imageRef = useRef(null)
  const videoRef = useRef(null)
  const videoContainerRef = useRef(null)
  const [zoomMode, setZoomMode] = useState("none") // 'none', 'scale', 'lens'
  const [invalidDropdowns, setInvalidDropdowns] = useState({})

  // Memoize lookup options - Move this before the validation effect
  const lookupOptions = useMemo(() => {
    const getLookupOptions = table => {
      return appLookups?.filter(lookup => lookup.Table === table) || []
    }

    return {
      branch: getLookupOptions("Branch"),
      usage: getLookupOptions("Usages"),
      fileKind: getLookupOptions("FilesKind"),
      measures: getLookupOptions("Measures"),
      extras: getLookupOptions("Extras"),
      colors: getLookupOptions("Colors"),
      conditions: getLookupOptions("Conditions"),
      subStatus: getLookupOptions("SubStatus")
    }
  }, [appLookups])

  // Add validation function
  const validateDropdownValue = useCallback((field, value, options) => {
    if (value === undefined || value === null || value === "") return true
    return options.some(option => option.Code === value)
  }, [])

  // Add validation effect
  useEffect(() => {
    if (!inspection || !lookupOptions) return

    const newInvalidDropdowns = {}
    let hasInvalidValues = false

    // Validate vehicle usage
    if (!validateDropdownValue("UsageCode", inspection.Vehicle?.UsageCode, lookupOptions.usage)) {
      newInvalidDropdowns.usage = true
      hasInvalidValues = true
      toast.error(`Μη έγκυρη τιμή για Χρήση Οχήματος: ${inspection.Vehicle?.UsageCode}`)
    }

    // Validate vehicle color
    if (!validateDropdownValue("ColorCode", inspection.Vehicle?.ColorCode, lookupOptions.colors)) {
      newInvalidDropdowns.color = true
      hasInvalidValues = true
      toast.error(`Μη έγκυρη τιμή για Χρώμα Οχήματος: ${inspection.Vehicle?.ColorCode}`)
    }

    if (hasInvalidValues) {
      setInvalidDropdowns(newInvalidDropdowns)
    }
  }, [inspection, lookupOptions, validateDropdownValue])

  // Add this useEffect for container measurements
  useEffect(() => {
    const updateContainerSize = () => {
      if (carContainerRef.current) {
        const { width, height } = carContainerRef.current.getBoundingClientRect()
        setContainerSize({ width, height })
      }
    }

    // Initial measurement with a small delay to ensure DOM is ready
    const initialMeasurement = setTimeout(updateContainerSize, 0)
    window.addEventListener("resize", updateContainerSize)

    return () => {
      clearTimeout(initialMeasurement)
      window.removeEventListener("resize", updateContainerSize)
    }
  }, [])

  // Add this useEffect to re-measure when inspection data changes
  useEffect(() => {
    if (carContainerRef.current) {
      const { width, height } = carContainerRef.current.getBoundingClientRect()
      setContainerSize({ width, height })
    }
  }, [inspection])

  // Fetch users for expert dropdown
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await get("/users", { params: { RoleID: 1, Active: 1 } }) // RoleID 1 is for experts, only active experts
        if (response.success && response.data?.Results?.APIuser) {
          setUsers(response.data.Results.APIuser)
        }
      } catch (err) {
        console.error("Failed to fetch users:", err)
      }
    }
    fetchUsers()
  }, [get])

  // Helper function to handle array-based fields (measures, extras)
  const handleArrayFieldChange = (field, value) => {
    if (
      field === "Vehicle.ProtectionMeasuresList" ||
      field === "Vehicle.ExtraEquipmentList" ||
      field === "Vehicle.PreExistingDamagesList"
    ) {
      // Special handling for protection measures and extra equipment which use numeric codes
      const currentValues =
        field === "Vehicle.ProtectionMeasuresList"
          ? inspection.Vehicle.ProtectionMeasuresList?.short || []
          : field === "Vehicle.ExtraEquipmentList"
          ? inspection.Vehicle.ExtraEquipmentList?.short || []
          : inspection.Vehicle.PreExistingDamagesList?.short || []
      const valueIndex = currentValues.indexOf(value)
      const newValues = [...currentValues]

      if (valueIndex === -1) {
        newValues.push(value)
      } else {
        newValues.splice(valueIndex, 1)
      }

      // Store internally as { short: [1,2,3] } for easier UI handling
      handleInputChange(field, { short: newValues })
    } else {
      // Original handling for other array fields like ExtraEquipment
      const pathParts = field.split(".")
      const currentValues = pathParts.reduce((obj, part) => obj?.[part], inspection) || []

      // Convert string to array if needed
      const values = Array.isArray(currentValues)
        ? currentValues
        : currentValues
            .split(",")
            .map(v => v.trim())
            .filter(Boolean)

      const valueIndex = values.indexOf(value)
      const newValues = [...values]

      if (valueIndex === -1) {
        newValues.push(value)
      } else {
        newValues.splice(valueIndex, 1)
      }

      handleInputChange(field, newValues)
    }
  }

  const refreshInspectionDetails = useCallback(
    async (doRefreshCalendar = true) => {
      try {
        setLoading(true)
        const response = await get(`/inspections/${inspectionId}`)
        setInspection(response.data)
        if (doRefreshCalendar) {
          refreshCalendar()
        }
      } catch (error) {
        console.error("Error refreshing inspection details:", error)
        toast.error("Αποτυχία ανανέωσης στοιχείων ελέγχου")
      } finally {
        setLoading(false)
      }
    },
    [get, inspectionId, setInspection, setLoading, refreshCalendar]
  )

  // Effect to handle dynamic mode changes
  useEffect(() => {
    if (dynamic) {
      setInspectionId(null)
      setInspection(null)
      setLoading(false)
    }
  }, [dynamic])

  useEffect(() => {
    if (inspectionId) {
      refreshInspectionDetails(false)
    }
  }, [inspectionId]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleCancelInspection = async () => {
    setSelectedSubStatus("")
    setRejectionComments("")
    setShowRejectionModal(true)
  }

  const handleConfirmCancelInspection = async () => {
    if (!selectedSubStatus) {
      toast.error("Παρακαλώ επιλέξτε αιτία απόρριψης")
      return
    }

    try {
      setIsSubmitting({ status: true, action: "cancelInspection" })
      const response = await put(`/inspections/${inspectionId}`, {
        InspectionID: inspectionId,
        Status: 99,
        SubStatusRejected: selectedSubStatus,
        Comments: rejectionComments,
        actionType: 90
      })

      // Check if the nested response indicates failure
      if (!response.data?.IsSuccess) {
        throw new Error(response.data?.Problem || "Αποτυχία ακύρωσης ελέγχου")
      }

      // Refresh the inspection data after cancellation
      await refreshInspectionDetails()
      setRejectionComments("")
      setSelectedSubStatus("")
      toast.success("Ο έλεγχος ακυρώθηκε επιτυχώς", { autoClose: 3000 })
    } catch (error) {
      console.error("Error canceling inspection:", error)
      toast.error(error.message || "Αποτυχία ακύρωσης ελέγχου")
    } finally {
      setIsSubmitting({ status: false, action: null })
      setShowRejectionModal(false)
    }
  }

  const handleRestore = async () => {
    try {
      setIsSubmitting({ status: true, action: "restore" })
      // Set status to 1 if expert is assigned, 0 if not
      const newStatus = inspection.ExpertID ? 1 : 0
      const response = await put(`/inspections/${inspectionId}`, {
        InspectionID: inspectionId,
        Status: newStatus,
        actionType: 91
      })

      // Check if the nested response indicates failure
      if (!response.data?.IsSuccess) {
        throw new Error(response.data?.Problem || "Αποτυχία επαναφοράς ελέγχου")
      }

      // Refresh the inspection data after restoration
      await refreshInspectionDetails()
      toast.success("Ο έλεγχος επαναφέρθηκε επιτυχώς", { autoClose: 3000 })
    } catch (error) {
      console.error("Error restoring inspection:", error)
      toast.error("Αποτυχία επαναφοράς ελέγχου")
    } finally {
      setIsSubmitting({ status: false, action: null })
    }
  }

  const handleCancelAssignment = async () => {
    setSelectedSubStatus("")
    setCancelAssignmentComments("")
    setShowCancelAssignmentModal(true)
  }

  const handleConfirmCancelAssignment = async () => {
    if (!selectedSubStatus) {
      toast.error("Παρακαλώ επιλέξτε αιτία ακύρωσης")
      return
    }

    try {
      setIsSubmitting({ status: true, action: "cancelAssignment" })
      const response = await put(`/inspections/${inspectionId}`, {
        InspectionID: inspectionId,
        SubStatusRejected: selectedSubStatus,
        Comments: cancelAssignmentComments,
        actionType: 23
      })

      // Check if the nested response indicates failure
      if (!response.data?.IsSuccess) {
        throw new Error(response.data?.Problem || "Αποτυχία ακύρωσης ανάθεσης")
      }

      if (!isAdmin()) {
        // For non-admin users, navigate to home
        setShowCancelAssignmentModal(false)
        setCancelAssignmentComments("")
        setSelectedSubStatus("")
        refreshCalendar()
        toast.success("Η ανάθεση ακυρώθηκε επιτυχώς", { autoClose: 3000 })
        navigate("/")
      } else {
        // For admin users, refresh the inspection data
        await refreshInspectionDetails()
        setShowCancelAssignmentModal(false)
        setCancelAssignmentComments("")
        setSelectedSubStatus("")
        toast.success("Η ανάθεση ακυρώθηκε επιτυχώς", { autoClose: 3000 })
      }
    } catch (error) {
      console.error("Error canceling assignment:", error)
      toast.error("Αποτυχία ακύρωσης ανάθεσης")
    } finally {
      setIsSubmitting({ status: false, action: null })
      setShowCancelAssignmentModal(false)
    }
  }

  const handleSave = async () => {
    try {
      setIsSubmitting({ status: true, action: "save" })
      // Create a copy of the inspection object for sending
      const inspectionToSend = { ...inspection }
      inspectionToSend.Vehicle = { ...inspection.Vehicle }

      // Remove properties that should not be sent
      delete inspectionToSend.InspectionHistoryList
      delete inspectionToSend.InspectionFilesList
      delete inspectionToSend.CustomerID
      delete inspectionToSend.IssueDateTime
      delete inspectionToSend.InsuranceCompanyID

      // Format dates in the required API format
      if (inspectionToSend.ExpertAssignmentDate) {
        inspectionToSend.ExpertAssignmentDate = formatDateForApi(inspectionToSend.ExpertAssignmentDate)
      }
      if (inspectionToSend.IssueDateTime) {
        inspectionToSend.IssueDateTime = formatDateForApi(inspectionToSend.IssueDateTime)
      }
      if (inspectionToSend.CompletionDateTime) {
        inspectionToSend.CompletionDateTime = formatDateForApi(inspectionToSend.CompletionDateTime)
      }

      // Convert from { short: [1,2,3] } to [{ short: 1 }, { short: 2 }, { short: 3 }]
      inspectionToSend.Vehicle.ProtectionMeasuresList = (
        inspection.Vehicle.ProtectionMeasuresList?.short || []
      ).map(code => ({ short: code }))

      // Convert from { short: [1,2,3] } to [{ short: 1 }, { short: 2 }, { short: 3 }]
      inspectionToSend.Vehicle.ExtraEquipmentList = (inspection.Vehicle.ExtraEquipmentList?.short || []).map(
        code => ({ short: code })
      )

      // Convert from { short: [1,2,3] } to [{ short: 1 }, { short: 2 }, { short: 3 }]
      inspectionToSend.Vehicle.PreExistingDamagesList = (
        inspection.Vehicle.PreExistingDamagesList?.short || []
      ).map(code => ({ short: code }))

      const response = await put(`/inspections/${inspectionId}`, inspectionToSend)

      // Check if the nested response indicates failure
      if (!response.data?.IsSuccess) {
        throw new Error(response.data?.Problem || "Αποτυχία αποθήκευσης αλλαγών")
      }

      await refreshInspectionDetails()
      toast.success("Οι αλλαγές αποθηκεύτηκαν επιτυχώς", { autoClose: 3000 })
    } catch (err) {
      toast.error(err.message || "Αποτυχία αποθήκευσης αλλαγών")
    } finally {
      setIsSubmitting({ status: false, action: null })
    }
  }

  const handleInputChange = (path, value) => {
    setInspection(prev => {
      const newInspection = { ...prev }
      const pathParts = path.split(".")
      let current = newInspection

      for (let i = 0; i < pathParts.length - 1; i++) {
        current = current[pathParts[i]]
      }

      current[pathParts[pathParts.length - 1]] = value
      return newInspection
    })
  }

  const handleAssignInspection = () => {
    setSelectedExpertId("")
    setAssignmentDateTime(new Date().toISOString())
    setShowAssignmentModal({ show: true, type: "new" })
  }

  const handleChangeAssignment = () => {
    setSelectedExpertId(inspection.ExpertID?.toString() || "")
    setAssignmentDateTime("")
    setShowAssignmentModal({ show: true, type: "change" })
  }

  const handleConfirmAssignment = async () => {
    if (!selectedExpertId) {
      toast.error("Παρακαλώ επιλέξτε πραγματογνώμονα")
      return
    }

    // For change assignment, validate that a different expert is selected
    if (inspection.Status === 1 && selectedExpertId === inspection.ExpertID?.toString()) {
      toast.error("Παρακαλώ επιλέξτε διαφορετικό πραγματογνώμονα")
      return
    }

    try {
      setIsSubmitting({ status: true, action: "assignment" })

      const payload = {
        InspectionID: inspectionId,
        ExpertID: parseInt(selectedExpertId, 10),
        actionType: inspection.Status === 1 ? 22 : 21
      }

      // Only add ExpertAssignmentDate if it has a value
      if (assignmentDateTime) {
        payload.ExpertAssignmentDate = formatDateForApi(assignmentDateTime)
      }

      const response = await put(`/inspections/${inspectionId}`, payload)

      // Check if the nested response indicates failure
      if (!response.data?.IsSuccess) {
        throw new Error(response.data?.Problem || "Αποτυχία ανάθεσης ελέγχου")
      }

      // Refresh the inspection data after assignment
      await refreshInspectionDetails()
      setSelectedExpertId("")
      setAssignmentDateTime("")
      toast.success("Η ανάθεση ολοκληρώθηκε επιτυχώς", { autoClose: 3000 })
    } catch (error) {
      console.error("Error assigning inspection:", error)
      toast.error("Αποτυχία ανάθεσης ελέγχου")
    } finally {
      setIsSubmitting({ status: false, action: null })
      setShowAssignmentModal({ show: false, type: null })
    }
  }

  const isPreviewableFile = fileName => {
    return fileName.toLowerCase().match(/\.(jpg|jpeg|png|gif|pdf|webm)$/) !== null
  }

  const handleFileDownload = async file => {
    // Show modal immediately for previewable files
    if (isPreviewableFile(file.FileName)) {
      const previewableFiles = inspection.InspectionFilesList.InspectionFilesDetails.filter(f =>
        isPreviewableFile(f.FileName)
      )
      const index = previewableFiles.findIndex(f => f.FileID === file.FileID)
      setPreviewIndex(index)
      setPreviewFile(file)

      // Check if file is already cached
      if (fileCache[file.FileID]) {
        setPreviewUrl(fileCache[file.FileID])
        setShowPreviewModal(true)
        return
      }
      setPreviewUrl(null)
      setShowPreviewModal(true)
    } else {
      setDownloadingFileId(file.FileID)
    }
    setIsSubmitting({ status: true, action: "download" })

    try {
      const response = await get(`/inspections/${inspectionId}/files/${file.FileID}/${file.FileName}`, {
        responseType: "blob",
        transformResponse: [data => data]
      })

      // Get content type from headers or determine from file extension
      const contentType =
        response.headers?.get("Content-Type") ||
        (file.FileName.toLowerCase().endsWith(".pdf")
          ? "application/pdf"
          : file.FileName.toLowerCase().endsWith(".webm")
          ? "video/webm"
          : file.FileName.toLowerCase().match(/\.(jpg|jpeg|png|gif)$/)
          ? "image/" + file.FileName.toLowerCase().split(".").pop()
          : "application/octet-stream")

      // Create a proper blob with the content type
      const blob = new Blob([response.data], { type: contentType })
      const url = window.URL.createObjectURL(blob)

      if (isPreviewableFile(file.FileName)) {
        // Cache the file URL
        setFileCache(prev => ({
          ...prev,
          [file.FileID]: url
        }))
        setPreviewUrl(url)
      } else {
        const link = document.createElement("a")
        link.href = url
        link.setAttribute("download", file.FileName)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      }
    } catch (error) {
      console.error("Error downloading file:", error)
      toast.error("Αποτυχία λήψης αρχείου")
      setShowPreviewModal(false)
    } finally {
      setIsSubmitting({ status: false, action: null })
      setDownloadingFileId(null)
    }
  }

  const handleClosePreview = () => {
    setShowPreviewModal(false)
    setPreviewFile(null)
    setPreviewUrl(null)
    setPreviewIndex(0)
  }

  const handleDownloadFromPreview = () => {
    if (previewFile && previewUrl) {
      const link = document.createElement("a")
      link.href = previewUrl
      link.setAttribute("download", previewFile.FileName)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }
  }

  const handleDeleteFile = file => {
    setFileToDelete(file)
    setShowDeleteFileModal(true)
  }

  const handleConfirmDeleteFile = async () => {
    try {
      setIsSubmitting({ status: true, action: "delete" })
      const response = await deleteFile(`/inspections/${inspectionId}/files/${fileToDelete.FileID}`)
      if (response.success) {
        // Update the inspection state directly by removing the deleted file
        setInspection(prev => ({
          ...prev,
          InspectionFilesList: {
            ...prev.InspectionFilesList,
            InspectionFilesDetails: prev.InspectionFilesList.InspectionFilesDetails.filter(
              file => file.FileID !== fileToDelete.FileID
            )
          }
        }))

        // Clean up the file cache if it exists
        if (fileCache[fileToDelete.FileID]) {
          URL.revokeObjectURL(fileCache[fileToDelete.FileID])
          setFileCache(prev => {
            const newCache = { ...prev }
            delete newCache[fileToDelete.FileID]
            return newCache
          })
        }

        setShowDeleteFileModal(false)
        setFileToDelete(null)
        toast.success("Το αρχείο διαγράφηκε επιτυχώς", { autoClose: 3000 })
      }
    } catch (err) {
      console.error("Error deleting file:", err)
      toast.error("Αποτυχία διαγραφής αρχείου")
    } finally {
      setIsSubmitting({ status: false, action: null })
    }
  }

  const handleFileUpload = async (fileData, fromEexpert = false) => {
    const { fileName } = fileData

    try {
      setIsSubmitting({ status: true, action: "upload" })
      const response = await post(`/inspections/${inspectionId}/files`, {
        files: [fileData]
      })

      if (response.success && response.data?.IsSuccess) {
        if (!fromEexpert) {
          toast.success(fileName + " - Το αρχείο μεταφορτώθηκε επιτυχώς", { autoClose: 3000 })
          await refreshInspectionDetails(false)
          setShowFileUpload(false)
        }
      } else {
        // Show the specific error message from the API
        const errorMessage = fileName + " - " + response.data?.Problem || "Αποτυχία μεταφόρτωσης αρχείου"
        toast.error(errorMessage)
      }
      return response
    } catch (err) {
      console.error("Error uploading file:", err)
      toast.error(fileName + " - Αποτυχία μεταφόρτωσης αρχείου")
      return { success: false, data: { IsSuccess: false } }
    } finally {
      setIsSubmitting({ status: false, action: null })
    }
  }

  const handleCompleteInspection = async () => {
    try {
      setIsSubmitting({ status: true, action: "complete" })

      // Create a copy of the inspection object to send
      const inspectionToSend = { ...inspection }

      // Remove the lists that shouldn't be sent
      delete inspectionToSend.InspectionHistoryList
      delete inspectionToSend.InspectionFilesList

      // Add the status for completion
      inspectionToSend.Status = 10

      // Add the action type for completion
      inspectionToSend.actionType = 100

      // Format dates properly
      if (inspectionToSend.IssueDateTime) {
        inspectionToSend.IssueDateTime = formatDateForApi(inspectionToSend.IssueDateTime)
      }
      if (inspectionToSend.ExpertAssignmentDate) {
        inspectionToSend.ExpertAssignmentDate = formatDateForApi(inspectionToSend.ExpertAssignmentDate)
      }
      if (inspectionToSend.CompletionDateTime) {
        inspectionToSend.CompletionDateTime = formatDateForApi(inspectionToSend.CompletionDateTime)
      }

      // Convert from { short: [1,2,3] } to [{ short: 1 }, { short: 2 }, { short: 3 }]
      inspectionToSend.Vehicle.ProtectionMeasuresList = (
        inspection.Vehicle.ProtectionMeasuresList?.short || []
      ).map(code => ({ short: code }))

      // Convert from { short: [1,2,3] } to [{ short: 1 }, { short: 2 }, { short: 3 }]
      inspectionToSend.Vehicle.ExtraEquipmentList = (inspection.Vehicle.ExtraEquipmentList?.short || []).map(
        code => ({ short: code })
      )

      // Convert from { short: [1,2,3] } to [{ short: 1 }, { short: 2 }, { short: 3 }]
      inspectionToSend.Vehicle.PreExistingDamagesList = (
        inspection.Vehicle.PreExistingDamagesList?.short || []
      ).map(code => ({ short: code }))

      const response = await put(`/inspections/${inspectionId}`, inspectionToSend)

      if (!response.data?.IsSuccess) {
        throw new Error(response.data?.Problem || "Αποτυχία ολοκλήρωσης ελέγχου")
      }

      // Generate PDF
      const pdf = await handlePrintInspection(false, true)
      if (!pdf) {
        throw new Error("Αποτυχία δημιουργίας PDF")
      }

      // Get the base64 data directly from the PDF output
      const pdfData = pdf.output("datauristring").split(",")[1]

      // Send the PDF to the server
      await post("/send-inspection", {
        inspectionId,
        pdfBase64: pdfData,
        email: inspection.CustomerMail
      })

      // Refresh the inspection data
      await refreshInspectionDetails()
      toast.success("Ο έλεγχος ολοκληρώθηκε επιτυχώς", { autoClose: 3000 })
    } catch (error) {
      console.error("Error completing inspection:", error)
      toast.error(error.message || "Αποτυχία ολοκλήρωσης ελέγχου")
    } finally {
      setIsSubmitting({ status: false, action: null })
    }
  }

  const handleStartEExpert = useCallback(() => {
    setNotificationPhone(inspection?.Owner?.Mobile ?? "")
    setNotificationEmail(inspection?.Owner?.Mail ?? "")
    setNotificationPrefs({
      sms: false,
      email: false
    })
    setShowNotificationModal(true)
  }, [inspection?.Owner?.Mobile, inspection?.Owner?.Mail])

  const handleConfirmEExpert = async () => {
    try {
      setIsModalSubmitting(true)

      // Generate session ID using Web Crypto API
      const timestamp = Date.now().toString()
      // Use expertId instead of inspectionId when dynamic=true
      const idToUse = dynamic ? user?.id.toString() : inspectionId
      const data = `${idToUse.toString()}-${timestamp}`
      const encoder = new TextEncoder()
      const hashBuffer = await crypto.subtle.digest("SHA-256", encoder.encode(data))
      const hashArray = Array.from(new Uint8Array(hashBuffer))
      const sessionId = hashArray
        .map(b => b.toString(16).padStart(2, "0"))
        .join("")
        .substring(0, 16)

      // Check if any notification method is selected
      const hasNotificationMethod = notificationPrefs.sms || notificationPrefs.email

      // Only call the API if a notification method is selected
      if (hasNotificationMethod) {
        const payload = {
          sessionLink: `${process.env.REACT_APP_APP_URL}/user/${idToUse}/${sessionId}`,
          inspectionId: dynamic ? "0" : inspectionId
        }

        // Add notification preferences to payload if selected
        if (notificationPrefs.sms) {
          payload.mobile = notificationPhone ?? inspection.Owner.Mobile
        }
        if (notificationPrefs.email) {
          payload.email = notificationEmail ?? inspection.Owner.Mail
        }

        const invitationResponse = await post("/send-session-invitation", payload)
        if (!invitationResponse.success) {
          if ("IsSuccess" in (invitationResponse.data || {}) && !invitationResponse.data.IsSuccess) {
            toast.error(invitationResponse.data?.Problem)
          }
          return
        }
        toast.success("Πρόσκληση συνεδρίας στάλθηκε επιτυχώς", { autoClose: 3000 })
      }

      // Add history entry only if not dynamic
      if (!dynamic) {
        const notificationText = hasNotificationMethod
          ? `Έναρξη διαδικασίας ExpertCall. Ειδοποίηση μέσω ${[
              notificationPrefs.sms && "SMS",
              notificationPrefs.email && "Email"
            ]
              .filter(Boolean)
              .join(" και ")}.`
          : "Έναρξη διαδικασίας ExpertCall."

        const historyResponse = await post(`/inspections/${inspectionId}/history`, {
          Descr: notificationText
        })

        if (!historyResponse.success || !historyResponse.data?.IsSuccess) {
          throw new Error(historyResponse.data?.Problem || "Αποτυχία καταχώρησης ιστορικού")
        }
      }

      setShowNotificationModal(false)
      setShowEExpert(true)
      setSessionId(sessionId)
    } catch (error) {
      console.error("Failed to start e-expert session:", error)
      toast.error(error.message || "Αποτυχία έναρξης συνεδρίας ExpertCall.")
    } finally {
      setIsModalSubmitting(false)
    }
  }

  const handlePrint = async includePhotos => {
    try {
      await handlePrintInspection(includePhotos)
      setShowPrintModal(false) // Assuming you have this state for modal visibility
    } catch (error) {
      console.error("Error printing:", error)
    }
  }

  // Update the existing print handler
  const handlePrintInspection = async (includePhotos = false, returnPdf = false) => {
    let pdf = null

    setIsPrinting(true)
    try {
      setPrintProgress("Δημιουργία κύριου PDF...")
      // Create a temporary div for PDF content
      const printDiv = document.createElement("div")
      printDiv.style.width = "210mm" // A4 width
      printDiv.style.padding = "10mm"
      printDiv.style.backgroundColor = "white"
      printDiv.style.position = "fixed"
      printDiv.style.left = "-9999px"
      document.body.appendChild(printDiv)

      // Create a clone of the car diagram for printing
      const carDiagramClone = document.createElement("div")
      carDiagramClone.className = "car-diagram-container-for-print"
      carDiagramClone.innerHTML = document.querySelector(".car-diagram-container").innerHTML
      document.body.appendChild(carDiagramClone)

      // Get the car diagram from the cloned element
      const carDiagramCanvas = await html2canvas(carDiagramClone, {
        scale: 0.5,
        useCORS: true,
        logging: false
      })

      // Remove the cloned element
      document.body.removeChild(carDiagramClone)

      // Add content
      printDiv.innerHTML = `
          <div style="font-family: Arial, sans-serif; font-size: 12px;">
              <!-- Header -->
              <div style="margin-bottom: 15px;">
                  <div style="text-align: right">
                      <img src="${require("../assets/images/logo.png")}" alt="Logo" style="
                      width: 173px;
                      height: 135px;
                      position: absolute;
                      top: -9px;
                      z-index: -1;
                      left: 23px;" />
                      <div>
                          <h3 style="margin: 0; color: #1F4E79;">Έκθεση Προ-ασφαλιστικού Ελέγχου Οχήματος</h3>
                          <p style="margin: 5px 0 0 0; font-size: 12px;">Αριθμός Ελέγχου: ${
                            inspection.InspectionID
                          }</p>
            </div>
          </div>
        </div>

              <!-- Owner Info -->
              <div style="margin-bottom: 10px;">
                  <h3 style="background-color: #1F4E79; color: white; padding: 5px; font-size: 14px;">Στοιχεία Ιδιοκτήτη</h3>
                  <div style="display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 5px;">
                      <div>
                          <p><strong>Όνομα:</strong> ${inspection.Owner?.Name || ""}</p>
                          <p><strong>Επώνυμο:</strong> ${inspection.Owner?.Surname || ""}</p>
                          <p><strong>Διεύθυνση:</strong> ${inspection.Owner?.Address || ""}</p>
                      </div>
                      <div>       
                          <p><strong>Πόλη:</strong> ${inspection.Owner?.City || ""}</p>
                          <p><strong>Τ.Κ.:</strong> ${inspection.Owner?.PostalCode || ""}</p>           
                          <p><strong>Τηλέφωνο:</strong> ${inspection.Owner?.Phone || ""}</p>
                      </div>
                      <div>
                          <p><strong>Κινητό:</strong> ${inspection.Owner?.Mobile || ""}</p>
                          <p><strong>ΑΦΜ:</strong> ${inspection.Owner?.TaxNumber || ""}</p>
                          <p><strong>Email:</strong> ${inspection.Owner?.Mail || ""}</p>
                      </div>
                  </div>
              </div>

              <!-- Vehicle Info -->
              <div style="margin-bottom: 10px;">
                  <h3 style="background-color: #1F4E79; color: white; padding: 5px; font-size: 14px;">Στοιχεία Οχήματος</h3>
                  <div style="display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 5px;">
                      <div>
                          <p><strong>Αριθμός Κυκλοφορίας:</strong> ${
                            inspection.Vehicle?.PlateNumber || ""
                          }</p>
                          <p><strong>Ασφαλιστική Εταιρία:</strong> ${inspection.InsuranceCompany || ""}</p>
                          <p><strong>Εργοστάσιο Κατασκευής:</strong> ${inspection.Vehicle?.Make || ""}</p>
                          <p><strong>Μοντέλο:</strong> ${inspection.Vehicle?.Model || ""}</p>
                          <p><strong>Έτος Κατασκευής:</strong> ${
                            inspection.Vehicle?.ConstructionYear || ""
                          }</p>
                          <p><strong>Φορολογήσιμη Ισχύς:</strong> ${inspection.Vehicle?.HP || ""}</p>
                      </div>
                      <div>
                          <p><strong>Κυβικά Εκατοστά:</strong> ${inspection.Vehicle?.CC || ""}</p>
                          <p><strong>Χρήση Οχήματος:</strong> ${
                            getAppLookupValue("Usages", inspection.Vehicle?.UsageCode) || ""
                          }</p>
                          <p><strong>Έτος Αγοράς Οχήματος:</strong> ${
                            inspection.Vehicle?.PurchaseYear || ""
                          }</p>
                          <p><strong>Αξία Οχήματος:</strong> ${inspection.Vehicle?.VehicleValue || ""}</p>
                          <p><strong>Θέσεις:</strong> ${inspection.Vehicle?.Seats || ""}</p>
                          <p><strong>Αριθμός Πλαισίου:</strong> ${inspection.Vehicle?.ChassisNumber || ""}</p>
                      </div>
                      <div>
                          <p><strong>Ένδειξη Χιλιομετρητή:</strong> ${
                            inspection.Vehicle?.OdometerCounter || ""
                          }</p>
                          <p><strong>Χρώμα Οχήματος:</strong> ${
                            getAppLookupValue("Colors", inspection.Vehicle?.ColorCode) || ""
                          }</p>
                          <p><strong>Πακέτο:</strong> ${inspection.Vehicle?.PackageName || ""}</p>
                          <p><strong>Απαλλαγή ΙΖ:</strong> ${inspection.Vehicle?.DeductibleΙΖ || ""}</p>
                          <p><strong>Κατάσταση Χρώματος:</strong> ${
                            getAppLookupValue("Conditions", inspection.Vehicle?.PaintConditionCode) || ""
                          }</p>
                          <p><strong>Γενική Κατάσταση Οχήματος:</strong> ${
                            getAppLookupValue("Conditions", inspection.Vehicle?.GeneralConditionCode) || ""
                          }</p>
                      </div>
                  </div>
                  <hr />
                  <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 5px;">
                      <div style="margin-top: 10px;">
                          <p><strong>Αναλυτική περιγραφή προϋπαρχουσών ζημιών:</strong></p>
                          <p>${inspection.Vehicle?.PreExistingDamages || ""}</p>
                      </div>
                      <div id="car-diagram-placeholder" style="position: relative;width: 100%;text-align: center;"></div>
                  </div>
              </div>

              <!-- Theft Protection -->
              <div style="margin-bottom: 15px;">
                  <h3 style="background-color: #1F4E79; color: white; padding: 5px; font-size: 14px;">Προστατευτικά Μέτρα Κλοπής</h3>
                  <div style="display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 5px;">
                      ${lookupOptions.measures
                        .map(
                          measure => `
                          <div style="display: flex; align-items: center; gap: 10px;">
                              <span style="min-width: 200px;">${measure.Name}</span>
                              <span style="color: ${
                                inspection.Vehicle?.ProtectionMeasuresList?.short?.includes(measure.Code)
                                  ? "green"
                                  : "red"
                              }">
                                  ${
                                    inspection.Vehicle?.ProtectionMeasuresList?.short?.includes(measure.Code)
                                      ? "✓"
                                      : "✗"
                                  }
                              </span>
                          </div>
                      `
                        )
                        .join("")}
                  </div>
                  ${
                    inspection.Vehicle?.ProtectionMeasures
                      ? `
                      <div style="margin-top: 10px;">
                          <p><strong>Αναφέρατε Λεπτομέρειες:</strong> ${inspection.Vehicle.ProtectionMeasures}</p>
                      </div>
                  `
                      : ""
                  }
              </div>

              <!-- Additional Equipment -->
              <div style="margin-bottom: 15px;">
                  <h3 style="background-color: #1F4E79; color: white; padding: 5px; font-size: 14px;">Πρόσθετα Εξαρτήματα</h3>
                  <div style="display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 5px;">
                      ${lookupOptions.extras
                        .map(
                          extra => `
                          <div style="display: flex; align-items: center; gap: 10px;">
                              <span style="min-width: 200px;">${extra.Name}</span>
                              <span style="color: ${
                                inspection.Vehicle?.ExtraEquipmentList?.short?.includes(extra.Code)
                                  ? "green"
                                  : "red"
                              }">
                                  ${
                                    inspection.Vehicle?.ExtraEquipmentList?.short?.includes(extra.Code)
                                      ? "✓"
                                      : "✗"
                                  }
                              </span>
                          </div>
                      `
                        )
                        .join("")}
                  </div>
                  ${
                    inspection.Vehicle?.ExtraEquipment
                      ? `
                      <div style="margin-top: 10px;">
                          <p><strong>Άλλο (αναφέρατε):</strong> ${inspection.Vehicle.ExtraEquipment}</p>
                      </div>
                  `
                      : ""
                  }
              </div>

              <!-- Comments -->
              <div style="margin-bottom: 15px;">
                  <h3 style="background-color: #1F4E79; color: white; padding: 5px; font-size: 14px;">Παρατηρήσεις</h3>
                  <div style="display: flex; align-items: center; gap: 10px;">
                      <span style="min-width: 200px;">${inspection.Comments || ""}</span>
                  </div>
              </div>

              <hr />
              <!-- Footer -->
              <div style="margin-top: 20px; display: flex; justify-content: space-between;">
                  <div>
                      <p><strong>Ημερομηνία Ελέγχου:</strong> ${new Date(
                        inspection.CompletionDateTime
                      ).toLocaleDateString("el-GR", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric"
                      })}</p>
                  </div>
                  <div>
                      <p><strong>Πραγματογνώμονας:</strong> ${inspection.ExpertName || ""}</p>
                  </div>
              </div>
          </div>
      `

      // If we have the car diagram, add it to the placeholder
      if (carDiagramCanvas) {
        const placeholder = printDiv.querySelector("#car-diagram-placeholder")
        const img = document.createElement("img")
        img.src = carDiagramCanvas.toDataURL("image/png")
        img.style.maxWidth = "100%"
        img.style.height = "auto"
        placeholder.appendChild(img)
      }

      // Create PDF document with A4 dimensions
      pdf = new jsPDF("p", "mm", "a4")
      const pageWidth = pdf.internal.pageSize.getWidth()
      const pageHeight = pdf.internal.pageSize.getHeight()
      const margin = 10 // 10mm margin

      // Convert the main content to canvas
      const canvas = await html2canvas(printDiv, {
        scale: 2,
        useCORS: true,
        logging: false
      })

      // Remove the temporary div
      //document.body.removeChild(printDiv)

      // Add the main content to PDF
      const imgData = canvas.toDataURL("image/jpeg", 1.0)
      pdf.addImage(imgData, "JPEG", 0, 0, pageWidth, 0, "", "FAST")

      // If photos are requested, add them
      if (includePhotos && inspection.InspectionFilesList?.InspectionFilesDetails?.length > 0) {
        // Filter only image files
        const imageFiles = inspection.InspectionFilesList.InspectionFilesDetails.filter(
          file =>
            file.FileKindID === 1 || // Assuming 1 is the ID for photos
            file.FileName?.toLowerCase().match(/\.(jpg|jpeg|png|gif)$/)
        )

        const fontResponse = await fetch(RobotoFont)
        const fontArrayBuffer = await fontResponse.arrayBuffer()

        // Convert ArrayBuffer to base64 string
        const fontBase64 = arrayBufferToBase64(fontArrayBuffer)

        pdf.addFileToVFS("Roboto.ttf", fontBase64)
        pdf.addFont("Roboto.ttf", "Roboto", "normal")

        pdf.setFont("Roboto")

        for (const [index, file] of imageFiles.entries()) {
          setPrintProgress(`Ενσωμάτωση εικόνας ${index + 1} στο PDF...`)

          try {
            // Add a new page for each photo
            pdf.addPage()

            // Use cached file if available, otherwise fetch it
            let imageData
            if (fileCache[file.FileID]) {
              const response = await fetch(fileCache[file.FileID])
              imageData = await response.blob()
              imageData = await new Promise(resolve => {
                const reader = new FileReader()
                reader.onloadend = () => resolve(reader.result)
                reader.readAsDataURL(imageData)
              })
            } else {
              const response = await get(
                `/inspections/${inspectionId}/files/${file.FileID}/${file.FileName}`,
                {
                  responseType: "blob",
                  transformResponse: [data => data]
                }
              )
              imageData = await new Promise(resolve => {
                const reader = new FileReader()
                reader.onloadend = () => resolve(reader.result)
                reader.readAsDataURL(response.data)
              })
            }

            // Create an image element and wait for it to load
            const img = new Image()
            await new Promise((resolve, reject) => {
              img.onload = resolve
              img.onerror = reject
              img.src = imageData
            })

            // Calculate dimensions to fit photo within margins while maintaining aspect ratio
            const imgRatio = img.width / img.height
            const maxWidth = pageWidth - 2 * margin
            const maxHeight = pageHeight - 4 * margin // Extra margin for caption

            let finalWidth, finalHeight
            if (imgRatio > maxWidth / maxHeight) {
              finalWidth = maxWidth
              finalHeight = maxWidth / imgRatio
            } else {
              finalHeight = maxHeight
              finalWidth = maxHeight * imgRatio
            }

            // Center the image horizontally
            const xOffset = margin + (maxWidth - finalWidth) / 2

            // Add the image
            pdf.addImage(imageData, "JPEG", xOffset, margin, finalWidth, finalHeight, "", "FAST")

            // Add caption below the image with proper Greek encoding
            pdf.setFontSize(10)
            const caption = getAppLookupValue("FilesKind", file.FileKindID)

            // Center and add the text
            const textWidth = (pdf.getStringUnitWidth(caption) * 10) / pdf.internal.scaleFactor
            const textX = margin + (maxWidth - textWidth) / 2
            pdf.text(caption, textX, margin + finalHeight + 10)
          } catch (photoError) {
            console.error("Error adding photo to PDF:", photoError)
            // Continue with next photo if one fails
            continue
          }
        }
      }
      setPrintProgress("Ολοκλήρωση δημιουργίας PDF...")

      if (!returnPdf) {
        const pdfOutput = pdf.output("bloburl")
        const link = document.createElement("a")
        link.href = pdfOutput
        link.download = `Έλεγχος_${inspection.InspectionID}.pdf`
        link.click()
        toast.success("Το PDF κατέβηκε επιτυχώς στη συσκευή σας", { autoClose: 3000 })
      }
    } catch (error) {
      console.error("Error generating PDF:", error)
      toast.error("Παρουσιάστηκε σφάλμα κατά τη δημιουργία του PDF")
    } finally {
      setIsPrinting(false)
      setPrintProgress("")
      if (returnPdf) {
        return pdf
      }
    }
  }

  // Helper function to convert ArrayBuffer to base64
  function arrayBufferToBase64(buffer) {
    let binary = ""
    const bytes = new Uint8Array(buffer)
    const len = bytes.byteLength
    for (let i = 0; i < len; i++) {
      binary += String.fromCharCode(bytes[i])
    }
    return window.btoa(binary)
  }

  // Update the damage marker positioning function
  const getDamageMarkerStyle = useCallback(index => {
    // Predefined positions for each marker
    const positions = {
      9: { left: "33%", top: "14%" }, // Front Left
      1: { left: "66%", top: "13%" }, // Front Right
      6: { left: "33%", top: "84%" }, // Back Left
      4: { left: "66%", top: "84%" }, // Back Right
      0: { left: "50%", top: "10%" }, // Front
      5: { left: "50%", top: "90%" }, // Back
      8: { left: "30%", top: "35%" }, // Left Side
      2: { left: "70%", top: "35%" }, // Right Side
      7: { left: "30%", top: "60%" }, // Center Left
      3: { left: "70%", top: "60%" }, // Center Right
      10: { left: "50%", top: "57%" } // Center
    }

    return positions[index] || { left: "50%", top: "50%" }
  }, [])

  // Reset showEExpert on mount/reload
  useEffect(() => {
    // If dynamic is true, set showEExpert to true and open notification modal
    if (dynamic) {
      setShowEExpert(true)
      handleStartEExpert()
    } else {
      setShowEExpert(false)
    }
  }, [dynamic, setShowEExpert]) // eslint-disable-line react-hooks/exhaustive-deps

  // Replace the debouncedSearch with a regular search function
  const handleSearch = async () => {
    if (!searchTerm.trim()) return

    try {
      setIsSearching(true)
      const response = await get(`/quickSearch?criterio=${encodeURIComponent(searchTerm.trim())}`)

      if (response.success && response.data?.QuickSearch) {
        if (response.data.QuickSearch.length > 1) {
          setSearchResults(response.data.QuickSearch)
          setShowSearchResultsModal(true)
        } else if (response.data.QuickSearch.length === 1) {
          const firstResult = response.data.QuickSearch[0]
          loadInspectionDetails(firstResult.InspectionID)
        } else {
          toast.warning("Δε βρέθηκαν αποτελέσματα", { autoClose: 3000 })
        }
      }
    } catch (error) {
      console.error("Search failed:", error)
      toast.error("Αποτυχία αναζήτησης")
    } finally {
      setIsSearching(false)
    }
  }

  // Add handler for Enter key
  const handleKeyPress = e => {
    if (e.key === "Enter") {
      handleSearch()
    }
  }

  // Add this new function to handle loading inspection details
  const loadInspectionDetails = async newInspectionId => {
    try {
      setShowSearchResultsModal(false)
      setLoading(true)
      // Update inspectionId if in dynamic mode
      if (dynamic) {
        setInspectionId(newInspectionId)
      }
      const detailsResponse = await get(`/inspections/${newInspectionId}`)
      if (detailsResponse.success) {
        setInspection(detailsResponse.data)
        setSearchResults([])
      }
    } catch (error) {
      console.error("Failed to load inspection details:", error)
      toast.error("Αποτυχία φόρτωσης λεπτομερειών ελέγχου")
    } finally {
      setLoading(false)
    }
  }

  // Add this new modal component right before the return statement
  const SearchResultsModal = () => {
    // Define columns for the DataTable
    const columns = [
      {
        header: "ID Ελέγχου",
        field: "InspectionID",
        type: "number"
      },
      {
        header: "Αρ. Κυκλοφορίας",
        field: "PlateNumber",
        type: "string"
      },
      {
        header: "Ημ. Καταχώρησης",
        field: "IssueDateTime",
        type: "date",
        render: item => formatDateForDisplay(item.IssueDateTime)
      },
      {
        header: "Κατάσταση",
        field: "Status",
        type: "number",
        sortValue: item => item.Status,
        render: item => (
          <Badge
            bg={
              item.Status === 0
                ? "secondary"
                : item.Status === 1
                ? "warning"
                : item.Status === 10
                ? "success"
                : item.Status === 99
                ? "danger"
                : "secondary"
            }>
            {getAppLookupValue("Status", item.Status) || "Άγνωστο"}
          </Badge>
        )
      },
      {
        header: "",
        field: "actions",
        render: item => (
          <Button variant="success" size="sm" onClick={() => loadInspectionDetails(item.InspectionID)}>
            <i className="fas fa-check me-2" />
            Επιλογή
          </Button>
        )
      }
    ]

    return (
      <Modal show={showSearchResultsModal} onHide={() => setShowSearchResultsModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Αποτελέσματα Αναζήτησης</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <DataTable
            columns={columns}
            data={searchResults}
            loading={isSearching}
            pagination={false}
            hover={true}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowSearchResultsModal(false)}>
            Ακύρωση
          </Button>
        </Modal.Footer>
      </Modal>
    )
  }

  // Add the handleDownloadAll function near the other file handling functions
  const handleDownloadAll = async () => {
    try {
      setIsSubmitting({ status: true, action: "downloadAll" })
      const zip = new JSZip()
      const files = inspection.InspectionFilesList.InspectionFilesDetails
      const totalFiles = files.length

      // Create a folder for each file type
      const folders = {}

      for (const [index, file] of files.entries()) {
        // Update submission status with progress
        setIsSubmitting(prev => ({
          ...prev,
          progress: `Λήψη Αρχείων... (${index + 1}/${totalFiles})`
        }))

        try {
          // Get the file kind name for the folder
          const fileKindName = getAppLookupValue("FilesKind", file.FileKindID)

          // Create folder if it doesn't exist
          if (!folders[fileKindName]) {
            folders[fileKindName] = zip.folder(fileKindName)
          }

          // Get the file content
          const response = await get(`/inspections/${inspectionId}/files/${file.FileID}/${file.FileName}`, {
            responseType: "blob",
            transformResponse: [data => data]
          })

          // Add file to its corresponding folder
          folders[fileKindName].file(file.FileName, response.data)
        } catch (error) {
          console.error(`Error downloading file ${file.FileName}:`, error)
          toast.error(`Αποτυχία λήψης αρχείου: ${file.FileName}`)
        }
      }

      // Generate the zip file
      const content = await zip.generateAsync({ type: "blob" })

      // Create download link
      const url = window.URL.createObjectURL(content)
      const link = document.createElement("a")
      link.href = url
      link.download = `Αρχεία_Ελέχου_${inspectionId}.zip`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      toast.success("Η λήψη των αρχείων ολοκληρώθηκε", { autoClose: 3000 })
    } catch (error) {
      console.error("Error downloading all files:", error)
      toast.error("Αποτυχία λήψης αρχείων")
    } finally {
      setIsSubmitting({ status: false, action: null })
    }
  }

  const handleNavigatePreview = async direction => {
    const previewableFiles = inspection.InspectionFilesList.InspectionFilesDetails.filter(file =>
      isPreviewableFile(file.FileName)
    )

    let newIndex = previewIndex + direction
    if (newIndex < 0) newIndex = previewableFiles.length - 1
    if (newIndex >= previewableFiles.length) newIndex = 0

    setPreviewIndex(newIndex)
    const nextFile = previewableFiles[newIndex]
    setPreviewFile(nextFile)

    // Check if file is already cached
    if (fileCache[nextFile.FileID]) {
      setPreviewUrl(fileCache[nextFile.FileID])
      return
    }

    setPreviewUrl(null)

    try {
      const response = await get(
        `/inspections/${inspectionId}/files/${nextFile.FileID}/${nextFile.FileName}`,
        {
          responseType: "blob",
          transformResponse: [data => data]
        }
      )

      const contentType =
        response.headers?.get("Content-Type") ||
        (nextFile.FileName.toLowerCase().endsWith(".pdf")
          ? "application/pdf"
          : nextFile.FileName.toLowerCase().endsWith(".webm")
          ? "video/webm"
          : nextFile.FileName.toLowerCase().match(/\.(jpg|jpeg|png|gif)$/)
          ? "image/" + nextFile.FileName.toLowerCase().split(".").pop()
          : "application/octet-stream")

      const blob = new Blob([response.data], { type: contentType })
      const url = window.URL.createObjectURL(blob)

      // Cache the file URL
      setFileCache(prev => ({
        ...prev,
        [nextFile.FileID]: url
      }))
      setPreviewUrl(url)
    } catch (error) {
      console.error("Error loading preview:", error)
      toast.error("Αποτυχία φόρτωσης αρχείου")
    }
  }

  return (
    <Layout
      showSidebar={true}
      inspectionHistory={inspection?.InspectionHistoryList?.InspectionHistoryDetails}
      loading={loading}
      showEExpert={showEExpert}
      inspectionId={dynamic ? user?.id : inspectionId}
      onCloseEExpert={() => setShowEExpert(false)}
      appLookups={appLookups}
      disableEExpertIframe={isSubmitting.status && isSubmitting.action === "eexpert-cancel"}
      sessionId={sessionId}
      onFileUpload={handleFileUpload}
      onComplete={handleCompleteInspection}
      refreshInspectionDetails={refreshInspectionDetails}
      isDynamic={dynamic}
      setLoading={setLoading}>
      {dynamic && (
        <div className="mb-3">
          <InputGroup>
            <Form.Control
              type="text"
              placeholder="Αναζήτηση Ελέγχου"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={isSearching || loading}
            />
            <Button
              className="pt-0 pb-0"
              variant="primary"
              onClick={handleSearch}
              disabled={isSearching || !searchTerm.trim()}>
              {isSearching ? (
                <>
                  <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                </>
              ) : (
                <>
                  <i className="fas fa-search" />
                </>
              )}
            </Button>
          </InputGroup>
        </div>
      )}

      {loading ? (
        <div className="content-glass">
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <div className="mt-2">Φόρτωση λεπτομερειών ελέγχου...</div>
          </div>
        </div>
      ) : !inspection && !dynamic ? (
        <div className="p-3">
          <div className="content-glass">
            <div className="alert alert-warning d-flex align-items-center m-0">
              <i className="fas fa-exclamation-triangle me-2" />Ο έλεγχος δε βρέθηκε.
            </div>
          </div>
        </div>
      ) : (
        <div className="content-glass mb-4">
          {inspection ? (
            <>
              {!dynamic && !showEExpert && <BackButton className="mb-4" />}
              <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap">
                <h5 className="mb-0">
                  Έλεγχος #{inspection.InspectionID}
                  <Badge
                    bg={
                      inspection.Status === 0
                        ? "secondary"
                        : inspection.Status === 1
                        ? "warning"
                        : inspection.Status === 10
                        ? "success"
                        : inspection.Status === 99
                        ? "danger"
                        : "secondary"
                    }
                    className="ms-2"
                    style={{
                      fontSize: "0.85rem",
                      padding: "0.5em 0.85em",
                      fontWeight: "500"
                    }}>
                    {getAppLookupValue("Status", inspection.Status) || "Άγνωστο"}
                    {inspection.Status === 99 && (
                      <div
                        className="fs-6 text-white mt-1"
                        style={{ opacity: 0.9, fontSize: "0.75rem !important" }}>
                        {getAppLookupValue("SubStatus", inspection.Status, inspection.SubStatusRejected) ||
                          "Άγνωστο"}
                      </div>
                    )}
                  </Badge>
                </h5>
                <div className="text-muted">
                  <small>
                    <i className="fas fa-calendar me-1" />
                    Ημ. Καταχώρησης: {formatDateForDisplay(inspection.IssueDateTime)}
                  </small>
                  {inspection.CompletionDateTime && (
                    <>
                      <br />
                      <small>
                        <i className="fas fa-check-circle me-1" />
                        Ημ. Ολοκλήρωσης: {formatDateForDisplay(inspection.CompletionDateTime)}
                      </small>
                    </>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="mb-4" data-container-query>
                <ButtonGroup className="d-flex flex-column flex-container-md-row gap-2">
                  {inspection.Status !== 99 && inspection.Status !== 10 && !showEExpert && (
                    <Button variant="primary" onClick={handleSave} disabled={isSubmitting.status}>
                      {isSubmitting.status && isSubmitting.action === "save" ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Αποθήκευση...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-save me-2" />
                          Διόρθωση
                        </>
                      )}
                    </Button>
                  )}
                  {inspection.Status === 0 && isAdmin() && !showEExpert && (
                    <Button
                      variant="outline-secondary"
                      onClick={handleAssignInspection}
                      disabled={isSubmitting.status}>
                      <>
                        <i className="fas fa-user-plus me-2" />
                        Ανάθεση Ελέγχου
                      </>
                    </Button>
                  )}
                  {inspection.Status === 1 && isAdmin() && !showEExpert && (
                    <Button
                      variant="outline-secondary"
                      onClick={handleChangeAssignment}
                      disabled={isSubmitting.status}>
                      {isSubmitting.status && isSubmitting.action === "changeAssignment" ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Αλλαγή...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-user-plus me-2" />
                          Αλλαγή Πραγματογνώμονα
                        </>
                      )}
                    </Button>
                  )}
                  {[1, 2].includes(inspection.Status) && !showEExpert && (
                    <Button
                      variant="outline-warning"
                      onClick={handleCancelAssignment}
                      disabled={isSubmitting.status}>
                      {isSubmitting.status && isSubmitting.action === "cancelAssignment" ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Ακύρωση Ανάθεσης...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-times me-2" />
                          Ακύρωση Ανάθεσης
                        </>
                      )}
                    </Button>
                  )}
                  {inspection.Status !== 99 && inspection.Status !== 10 && !showEExpert && (
                    <Button
                      variant="outline-danger"
                      onClick={handleCancelInspection}
                      disabled={isSubmitting.status}>
                      {isSubmitting.status && isSubmitting.action === "cancelInspection" ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Ακύρωση Ελέγχου...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-ban me-2" />
                          Ακύρωση Ελέγχου
                        </>
                      )}
                    </Button>
                  )}
                  {inspection.Status === 1 && !showEExpert && (
                    <Button
                      variant="outline-secondary"
                      onClick={handleStartEExpert}
                      disabled={isSubmitting.status}>
                      {isSubmitting.status &&
                      (isSubmitting.action === "eexpert-start" ||
                        isSubmitting.action === "eexpert-cancel") ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          "Έναρξη..."
                        </>
                      ) : (
                        <>
                          <i className={`fas fa-play me-2`} />
                          {"Έναρξη διαδικασίας ExpertCall"}
                        </>
                      )}
                    </Button>
                  )}
                  {[1, 2].includes(inspection.Status) && !showEExpert && (
                    <Button
                      variant="success"
                      onClick={handleCompleteInspection}
                      disabled={isSubmitting.status}>
                      {isSubmitting.status && isSubmitting.action === "complete" ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Ολοκλήρωση...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-check me-2" />
                          Ολοκλήρωση Ελέγχου
                        </>
                      )}
                    </Button>
                  )}
                  {inspection.Status === 99 && !showEExpert && (
                    <Button variant="outline-success" onClick={handleRestore} disabled={isSubmitting.status}>
                      {isSubmitting.status && isSubmitting.action === "restore" ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Επαναφορά...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-undo me-2" />
                          Επαναφορά
                        </>
                      )}
                    </Button>
                  )}
                  {inspection.Status === 10 && !showEExpert && (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setShowPrintModal(true)}
                      disabled={loading || isSubmitting.status}>
                      <i className="fas fa-print me-2" />
                      Εκτύπωση Ελέγχου
                    </Button>
                  )}
                </ButtonGroup>
              </div>

              {/* Rejection Comments Modal */}
              <Modal show={showRejectionModal} onHide={() => setShowRejectionModal(false)}>
                <Modal.Header closeButton>
                  <Modal.Title>
                    <i className="fas fa-ban me-2 text-danger" />
                    Ακύρωση Ελέγχου
                  </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                  <Form>
                    <Form.Group className="mb-3">
                      <Form.Label>Αιτία Ακύρωσης:</Form.Label>
                      <Form.Select
                        value={selectedSubStatus}
                        onChange={e => setSelectedSubStatus(e.target.value)}
                        isInvalid={!selectedSubStatus}>
                        <option value="">Επιλέξτε αιτία</option>
                        {lookupOptions.subStatus
                          .filter(status => 99 === status.Code)
                          .map(status => (
                            <option key={status.SubCode} value={status.SubCode}>
                              {status.Name}
                            </option>
                          ))}
                      </Form.Select>
                    </Form.Group>
                    <Form.Group>
                      <Form.Label>Σχόλια (προαιρετικά):</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        value={rejectionComments}
                        onChange={e => setRejectionComments(e.target.value)}
                        placeholder="Εισάγετε τα σχόλιά σας εδώ..."
                      />
                    </Form.Group>
                  </Form>
                </Modal.Body>
                <Modal.Footer>
                  <Button
                    variant="secondary"
                    onClick={() => setShowRejectionModal(false)}
                    disabled={isSubmitting.status}>
                    Ακύρωση
                  </Button>
                  <Button
                    variant="danger"
                    onClick={handleConfirmCancelInspection}
                    disabled={!selectedSubStatus || isSubmitting.status}>
                    {isSubmitting.status ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Ακύρωση Ελέγχου...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-ban me-2" />
                        Ακύρωση Ελέγχου
                      </>
                    )}
                  </Button>
                </Modal.Footer>
              </Modal>

              {/* Cancel Assignment Modal */}
              <Modal show={showCancelAssignmentModal} onHide={() => setShowCancelAssignmentModal(false)}>
                <Modal.Header closeButton>
                  <Modal.Title>
                    <i className="fas fa-times me-2 text-warning" />
                    Ακύρωση Ανάθεσης
                  </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                  <Form>
                    <Form.Group className="mb-3">
                      <Form.Label>Αιτία Ακύρωσης:</Form.Label>
                      <Form.Select
                        value={selectedSubStatus}
                        onChange={e => setSelectedSubStatus(e.target.value)}
                        isInvalid={!selectedSubStatus}>
                        <option value="">Επιλέξτε αιτία</option>
                        {lookupOptions.subStatus
                          .filter(status => 0 === status.Code)
                          .map(status => (
                            <option key={status.SubCode} value={status.SubCode}>
                              {status.Name}
                            </option>
                          ))}
                      </Form.Select>
                    </Form.Group>
                    <Form.Group>
                      <Form.Label>Σχόλια (προαιρετικά):</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        value={cancelAssignmentComments}
                        onChange={e => setCancelAssignmentComments(e.target.value)}
                        placeholder="Εισάγετε τα σχόλιά σας εδώ..."
                      />
                    </Form.Group>
                  </Form>
                </Modal.Body>
                <Modal.Footer>
                  <Button
                    variant="secondary"
                    onClick={() => setShowCancelAssignmentModal(false)}
                    disabled={isSubmitting.status}>
                    Ακύρωση
                  </Button>
                  <Button
                    variant="warning"
                    onClick={handleConfirmCancelAssignment}
                    disabled={!selectedSubStatus || isSubmitting.status}>
                    {isSubmitting.status ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Ακύρωση Ανάθεσης...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-times me-2" />
                        Ακύρωση Ανάθεσης
                      </>
                    )}
                  </Button>
                </Modal.Footer>
              </Modal>

              {/* Assignment Modal */}
              <Modal
                show={showAssignmentModal.show}
                onHide={() => setShowAssignmentModal({ show: false, type: null })}>
                <Modal.Header closeButton>
                  <Modal.Title>
                    <i
                      className={`fas ${
                        showAssignmentModal.type === "change" ? "fa-exchange-alt" : "fa-user-plus"
                      } me-2 ${showAssignmentModal.type === "change" ? "text-info" : "text-success"}`}
                    />
                    {showAssignmentModal.type === "change" ? "Αλλαγή Ανάθεσης" : "Ανάθεση Ελέγχου"}
                  </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                  <Form>
                    <Form.Group className="mb-3">
                      <Form.Label>Πραγματογνώμονας:</Form.Label>
                      <Form.Select
                        value={selectedExpertId}
                        onChange={e => setSelectedExpertId(e.target.value)}
                        isInvalid={!selectedExpertId}
                        disabled={isSubmitting.status}>
                        <option value="">Επιλέξτε πραγματογνώμονα</option>
                        {users.map(user => (
                          <option key={user.UserID} value={user.UserID}>
                            {user.Firstname} {user.LastName}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                    <Form.Group>
                      <Form.Label>Ημερομηνία & Ώρα ExpertCall (προαιρετικά):</Form.Label>
                      <DateTimePicker
                        value={assignmentDateTime}
                        onChange={value => setAssignmentDateTime(value)}
                        disabled={isSubmitting.status}
                        placeholder="Επιλέξτε ημερομηνία & ώρα ExpertCall..."
                      />
                    </Form.Group>
                  </Form>
                </Modal.Body>
                <Modal.Footer>
                  <Button
                    variant="outline-secondary"
                    onClick={() => setShowAssignmentModal({ show: false, type: null })}
                    disabled={isSubmitting.status}>
                    Ακύρωση
                  </Button>
                  <Button
                    variant={showAssignmentModal.type === "change" ? "outline-info" : "outline-success"}
                    onClick={handleConfirmAssignment}
                    disabled={
                      !selectedExpertId ||
                      isSubmitting.status ||
                      (showAssignmentModal.type === "change" &&
                        selectedExpertId === inspection.ExpertID?.toString())
                    }>
                    {isSubmitting.status ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        {showAssignmentModal.type === "change" ? "Αλλαγή..." : "Ανάθεση..."}
                      </>
                    ) : (
                      <>
                        <i
                          className={`fas ${
                            showAssignmentModal.type === "change" ? "fa-exchange-alt" : "fa-user-plus"
                          } me-2`}
                        />
                        {showAssignmentModal.type === "change" ? "Αλλαγή Ανάθεσης" : "Ανάθεση"}
                      </>
                    )}
                  </Button>
                </Modal.Footer>
              </Modal>

              {/* Delete File Confirmation Modal */}
              <Modal show={showDeleteFileModal} onHide={() => setShowDeleteFileModal(false)}>
                <Modal.Header closeButton>
                  <Modal.Title>
                    <i className="fas fa-trash me-2 text-danger" />
                    Διαγραφή Αρχείου
                  </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                  Είστε βέβαιοι ότι θέλετε να διαγράψετε το αρχείο "{fileToDelete?.FileName}";
                </Modal.Body>
                <Modal.Footer>
                  <Button
                    variant="secondary"
                    onClick={() => setShowDeleteFileModal(false)}
                    disabled={isSubmitting.status}>
                    Ακύρωση
                  </Button>
                  <Button variant="danger" onClick={handleConfirmDeleteFile} disabled={isSubmitting.status}>
                    {isSubmitting.status ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Διαγραφή...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-trash me-2" />
                        Διαγραφή
                      </>
                    )}
                  </Button>
                </Modal.Footer>
              </Modal>

              {/* Preview Modal */}
              <Modal show={showPreviewModal} onHide={handleClosePreview} size="lg">
                <Modal.Header closeButton>
                  <Modal.Title>
                    <i
                      className={`fas ${
                        previewFile?.FileName.toLowerCase().match(/\.(jpg|jpeg|png|gif)$/)
                          ? "fa-image"
                          : previewFile?.FileName.toLowerCase().endsWith(".pdf")
                          ? "fa-file-pdf"
                          : previewFile?.FileName.toLowerCase().endsWith(".webm")
                          ? "fa-file-video"
                          : "fa-file"
                      } me-2`}
                    />
                    {previewFile?.FileName}
                  </Modal.Title>
                </Modal.Header>
                <Modal.Body className="text-center position-relative">
                  {!previewUrl ? (
                    <div className="py-5">
                      <Spinner animation="border" variant="primary" />
                      <div className="text-muted mt-3">Φόρτωση αρχείου...</div>
                    </div>
                  ) : (
                    <>
                      {/* Navigation Arrows */}
                      <div
                        className="position-absolute top-50 start-0 translate-middle-y"
                        style={{ zIndex: 1000, left: "20px" }}>
                        <Button
                          variant="light"
                          className="rounded-circle shadow-sm"
                          onClick={() => handleNavigatePreview(-1)}
                          style={{
                            width: "40px",
                            height: "40px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            margin: "0 20px"
                          }}>
                          <i className="fas fa-chevron-left" />
                        </Button>
                      </div>
                      <div
                        className="position-absolute top-50 end-0 translate-middle-y"
                        style={{ zIndex: 1000, right: "20px" }}>
                        <Button
                          variant="light"
                          className="rounded-circle shadow-sm"
                          onClick={() => handleNavigatePreview(1)}
                          style={{
                            width: "40px",
                            height: "40px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            margin: "0 20px"
                          }}>
                          <i className="fas fa-chevron-right" />
                        </Button>
                      </div>

                      {/* Preview Content */}
                      {previewFile?.FileName?.toLowerCase().endsWith(".pdf") ? (
                        <iframe src={previewUrl} width="100%" height="600px" title="PDF Preview" />
                      ) : previewFile?.FileName.toLowerCase().endsWith(".webm") ? (
                        <div
                          className="position-relative"
                          style={{
                            maxWidth: "100%",
                            maxHeight: "600px",
                            overflow: "hidden"
                          }}
                          ref={videoContainerRef}
                          onMouseMove={e => {
                            if (zoomMode === "none" || !videoRef.current) return
                            const rect = videoRef.current.getBoundingClientRect()
                            const x = ((e.clientX - rect.left) / rect.width) * 100
                            const y = ((e.clientY - rect.top) / rect.height) * 100
                            setZoomPosition({ x, y })

                            if (zoomMode === "scale") {
                              videoRef.current.style.transformOrigin = `${x}% ${y}%`
                            }
                          }}
                          onMouseLeave={() => {
                            if (zoomMode === "lens") {
                              setZoomPosition({ x: 50, y: 50 })
                            }
                          }}>
                          <video
                            ref={videoRef}
                            controls
                            autoPlay={false}
                            style={{
                              maxWidth: "100%",
                              maxHeight: "70vh",
                              transform: zoomMode === "scale" ? `scale(${zoomLevel})` : "none",
                              cursor: zoomMode !== "none" ? "move" : "default",
                              transition: "transform 0.1s ease-out"
                            }}
                            src={previewUrl || `data:video/webm;base64,${previewFile?.FileByte}`}
                            className="shadow">
                            <source type="video/webm" />
                            Your browser does not support the video tag.
                          </video>
                          {zoomMode === "scale" && (
                            <div className="zoom-controls position-absolute top-0 end-0 m-3 bg-white rounded shadow p-2">
                              <Button
                                variant="light"
                                size="sm"
                                onClick={() => setZoomLevel(prev => Math.min(prev + 0.5, 3))}
                                className="me-2">
                                <i className="fas fa-plus" />
                              </Button>
                              <Button
                                variant="light"
                                size="sm"
                                onClick={() => setZoomLevel(prev => Math.max(prev - 0.5, 1))}>
                                <i className="fas fa-minus" />
                              </Button>
                            </div>
                          )}
                          {zoomMode === "lens" && (
                            <div
                              className="zoom-lens"
                              style={{
                                position: "absolute",
                                width: "150px",
                                height: "150px",
                                border: "2px solid #fff",
                                borderRadius: "50%",
                                pointerEvents: "none",
                                overflow: "hidden",
                                boxShadow: "0 0 10px rgba(0,0,0,0.5)",
                                left: `${zoomPosition.x}%`,
                                top: `${zoomPosition.y}%`,
                                transform: "translate(-50%, -50%)"
                              }}>
                              <video
                                src={previewUrl}
                                style={{
                                  position: "absolute",
                                  left: `${-zoomPosition.x}%`,
                                  top: `${-zoomPosition.y}%`,
                                  width: "300%",
                                  height: "300%",
                                  objectFit: "cover",
                                  objectPosition: `${zoomPosition.x}% ${zoomPosition.y}%`
                                }}
                                autoPlay={false}
                                muted
                              />
                            </div>
                          )}
                        </div>
                      ) : (
                        <div
                          className="position-relative"
                          style={{
                            maxWidth: "100%",
                            maxHeight: "600px",
                            overflow: "hidden"
                          }}
                          onMouseMove={e => {
                            if (zoomMode === "none" || !imageRef.current) return
                            const rect = imageRef.current.getBoundingClientRect()
                            const x = ((e.clientX - rect.left) / rect.width) * 100
                            const y = ((e.clientY - rect.top) / rect.height) * 100
                            setZoomPosition({ x, y })

                            if (zoomMode === "scale") {
                              imageRef.current.style.transformOrigin = `${x}% ${y}%`
                            }
                          }}
                          onMouseLeave={() => {
                            if (zoomMode === "lens") {
                              setZoomPosition({ x: 50, y: 50 })
                            }
                          }}>
                          <img
                            ref={imageRef}
                            src={previewUrl}
                            alt="Preview"
                            className="img-fluid"
                            style={{
                              maxHeight: "600px",
                              transform: zoomMode === "scale" ? `scale(${zoomLevel})` : "none",
                              cursor: zoomMode !== "none" ? "move" : "default",
                              transition: "transform 0.1s ease-out"
                            }}
                          />
                          {zoomMode === "scale" && (
                            <div className="zoom-controls position-absolute top-0 end-0 m-3 bg-white rounded shadow p-2">
                              <Button
                                variant="light"
                                size="sm"
                                onClick={() => setZoomLevel(prev => Math.min(prev + 0.5, 3))}
                                className="me-2">
                                <i className="fas fa-plus" />
                              </Button>
                              <Button
                                variant="light"
                                size="sm"
                                onClick={() => setZoomLevel(prev => Math.max(prev - 0.5, 1))}>
                                <i className="fas fa-minus" />
                              </Button>
                            </div>
                          )}
                          {zoomMode === "lens" && (
                            <div
                              className="zoom-lens"
                              style={{
                                position: "absolute",
                                width: "150px",
                                height: "150px",
                                border: "2px solid #fff",
                                borderRadius: "50%",
                                pointerEvents: "none",
                                overflow: "hidden",
                                boxShadow: "0 0 10px rgba(0,0,0,0.5)",
                                left: `${zoomPosition.x}%`,
                                top: `${zoomPosition.y}%`,
                                transform: "translate(-50%, -50%)"
                              }}>
                              <img
                                src={previewUrl}
                                alt="Zoomed"
                                style={{
                                  position: "absolute",
                                  left: `${-zoomPosition.x}%`,
                                  top: `${-zoomPosition.y}%`,
                                  width: "300%",
                                  height: "300%",
                                  objectFit: "cover",
                                  objectPosition: `${zoomPosition.x}% ${zoomPosition.y}%`
                                }}
                              />
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </Modal.Body>
                <Modal.Footer>
                  <div className="d-flex gap-2 w-100">
                    <div className="me-auto">
                      {(previewFile?.FileName.toLowerCase().match(/\.(jpg|jpeg|png|gif)$/) ||
                        previewFile?.FileName.toLowerCase().endsWith(".webm")) && (
                        <div className="d-flex gap-2">
                          <Button
                            variant={zoomMode !== "none" ? "primary" : "outline-primary"}
                            onClick={() => {
                              setZoomMode(prev => (prev === "none" ? "scale" : "none"))
                              if (zoomMode === "none") {
                                setZoomLevel(1)
                              }
                            }}>
                            <i
                              className={`fas ${zoomMode === "none" ? "fa-search" : "fa-search-minus"} me-2`}
                            />
                            {zoomMode === "none" ? "Ενεργοποίηση Μεγέθυνσης" : "Απενεργοποίηση Μεγέθυνσης"}
                          </Button>
                          {zoomMode !== "none" && (
                            <Button
                              variant="outline-primary"
                              onClick={() => {
                                setZoomMode(prev => (prev === "scale" ? "lens" : "scale"))
                                setZoomLevel(1)
                              }}>
                              <i
                                className={`fas ${
                                  zoomMode === "scale" ? "fa-search-plus" : "fa-search"
                                } me-2`}
                              />
                              {zoomMode === "scale" ? "Κλίμακα" : "Φακός"}
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                    <Button variant="secondary" onClick={handleClosePreview}>
                      <i className="fas fa-times me-2" />
                      Κλείσιμο
                    </Button>
                    <Button variant="primary" onClick={handleDownloadFromPreview} disabled={!previewUrl}>
                      <i className="fas fa-download me-2" />
                      Λήψη
                    </Button>
                  </div>
                </Modal.Footer>
              </Modal>

              <div className="row g-4">
                {/* Basic Information and Owner Information Container */}
                <div className="col-12">
                  <div className="row" data-container-query>
                    <div className="col-12 col-container-md-12 mb-4">
                      <Card>
                        <Card.Header className="d-flex align-items-center">
                          <i className="fas fa-info-circle me-2" />
                          <h6 className="mb-0">Βασικές Πληροφορίες</h6>
                        </Card.Header>
                        <Card.Body>
                          <Form>
                            <Row className="g-3">
                              <Col md={6}>
                                <Form.Group>
                                  <Form.Label>ID Ελέγχου</Form.Label>
                                  <Form.Control type="text" value={inspection.InspectionID} disabled />
                                </Form.Group>
                              </Col>
                              <Col md={6}>
                                <Form.Group>
                                  <Form.Label>Κατάσταση</Form.Label>
                                  <Form.Control
                                    type="text"
                                    value={getAppLookupValue("Status", inspection.Status)}
                                    disabled
                                  />
                                </Form.Group>
                              </Col>
                              {inspection.Status === 99 && (
                                <Col md={6}>
                                  <Form.Group>
                                    <Form.Label>Αιτία Απόρριψης</Form.Label>
                                    <Form.Select value={inspection.SubStatusRejected} disabled>
                                      <option value={inspection.SubStatusRejected}>
                                        {getAppLookupValue(
                                          "SubStatus",
                                          inspection.Status,
                                          inspection.SubStatusRejected
                                        )}
                                      </option>
                                    </Form.Select>
                                  </Form.Group>
                                </Col>
                              )}
                              <Col md={6}>
                                <Form.Group>
                                  <Form.Label>Κλάδος</Form.Label>
                                  <Form.Control
                                    type="text"
                                    value={getAppLookupValue("Branch", inspection.BranchID) || "-"}
                                    disabled
                                  />
                                </Form.Group>
                              </Col>
                              <Col md={6}>
                                <Form.Group>
                                  <Form.Label>Πραγματογνώμονας</Form.Label>
                                  <Form.Control type="text" value={inspection.ExpertName} disabled />
                                </Form.Group>
                              </Col>
                              <Col md={6}>
                                <Form.Group>
                                  <Form.Label>Ημ. ExpertCall</Form.Label>
                                  <DateTimePicker
                                    value={inspection.ExpertAssignmentDate}
                                    onChange={value => handleInputChange("ExpertAssignmentDate", value)}
                                    disabled={
                                      isSubmitting.status ||
                                      inspection.Status === 99 ||
                                      inspection.Status === 10 ||
                                      inspection.Status === 2
                                    }
                                  />
                                </Form.Group>
                              </Col>
                              <Col md={6}>
                                <Form.Group>
                                  <Form.Label>ID Πελάτη</Form.Label>
                                  <Form.Control type="text" value={inspection.CustomerID} disabled />
                                </Form.Group>
                              </Col>
                              <Col md={6}>
                                <Form.Group>
                                  <Form.Label>Ημ. Καταχώρησης</Form.Label>
                                  <DateTimePicker
                                    value={inspection.IssueDateTime}
                                    disabled={true}
                                    showClearButton={false}
                                  />
                                </Form.Group>
                              </Col>
                              <Col md={12}>
                                <Form.Group>
                                  <Form.Label>Παρατηρήσεις</Form.Label>
                                  <Form.Control
                                    as="textarea"
                                    rows={3}
                                    value={inspection.Comments}
                                    onChange={e => handleInputChange("Comments", e.target.value)}
                                    disabled={
                                      isSubmitting.status ||
                                      inspection.Status === 99 ||
                                      inspection.Status === 10
                                    }
                                  />
                                </Form.Group>
                              </Col>
                            </Row>
                          </Form>
                        </Card.Body>
                      </Card>
                    </div>

                    {/* Owner Information */}
                    <div className="col-12">
                      <Card>
                        <Card.Header className="bg-navy text-white">
                          <h6 className="mb-0">1. Στοιχεία Ιδιοκτήτη του Οχήματος</h6>
                        </Card.Header>
                        <Card.Body>
                          <Row className="g-3">
                            <Col md={6}>
                              <Form.Group>
                                <Form.Label>Όνομα*</Form.Label>
                                <Form.Control
                                  type="text"
                                  value={inspection.Owner.Name}
                                  onChange={e => handleInputChange("Owner.Name", e.target.value)}
                                  disabled={
                                    isSubmitting.status ||
                                    inspection.Status === 99 ||
                                    inspection.Status === 10
                                  }
                                  isInvalid={!inspection.Owner.Name}
                                  required
                                />
                              </Form.Group>
                            </Col>
                            <Col md={6}>
                              <Form.Group>
                                <Form.Label>Επώνυμο*</Form.Label>
                                <Form.Control
                                  type="text"
                                  value={inspection.Owner.Surname}
                                  onChange={e => handleInputChange("Owner.Surname", e.target.value)}
                                  disabled={
                                    isSubmitting.status ||
                                    inspection.Status === 99 ||
                                    inspection.Status === 10
                                  }
                                  isInvalid={!inspection.Owner.Surname}
                                  required
                                />
                              </Form.Group>
                            </Col>
                            <Col md={6}>
                              <Form.Group>
                                <Form.Label>Τηλέφωνο*</Form.Label>
                                <Form.Control
                                  type="text"
                                  value={inspection.Owner.Phone}
                                  onChange={e => handleInputChange("Owner.Phone", e.target.value)}
                                  disabled={
                                    isSubmitting.status ||
                                    inspection.Status === 99 ||
                                    inspection.Status === 10
                                  }
                                  isInvalid={!inspection.Owner.Phone}
                                  required
                                />
                              </Form.Group>
                            </Col>
                            <Col md={6}>
                              <Form.Group>
                                <Form.Label>Κινητό</Form.Label>
                                <Form.Control
                                  type="text"
                                  value={inspection.Owner.Mobile}
                                  onChange={e => handleInputChange("Owner.Mobile", e.target.value)}
                                  disabled={
                                    isSubmitting.status ||
                                    inspection.Status === 99 ||
                                    inspection.Status === 10
                                  }
                                />
                              </Form.Group>
                            </Col>
                            <Col md={6}>
                              <Form.Group>
                                <Form.Label>Email</Form.Label>
                                <Form.Control
                                  type="email"
                                  value={inspection.Owner.Mail}
                                  onChange={e => handleInputChange("Owner.Mail", e.target.value)}
                                  disabled={
                                    isSubmitting.status ||
                                    inspection.Status === 99 ||
                                    inspection.Status === 10
                                  }
                                />
                              </Form.Group>
                            </Col>
                            <Col md={6}>
                              <Form.Group>
                                <Form.Label>Διεύθυνση*</Form.Label>
                                <Form.Control
                                  type="text"
                                  value={inspection.Owner.Address}
                                  onChange={e => handleInputChange("Owner.Address", e.target.value)}
                                  disabled={
                                    isSubmitting.status ||
                                    inspection.Status === 99 ||
                                    inspection.Status === 10
                                  }
                                  isInvalid={!inspection.Owner.Address}
                                  required
                                />
                              </Form.Group>
                            </Col>
                            <Col md={6}>
                              <Form.Group>
                                <Form.Label>Πόλη*</Form.Label>
                                <Form.Control
                                  type="text"
                                  value={inspection.Owner.City}
                                  onChange={e => handleInputChange("Owner.City", e.target.value)}
                                  disabled={
                                    isSubmitting.status ||
                                    inspection.Status === 99 ||
                                    inspection.Status === 10
                                  }
                                  isInvalid={!inspection.Owner.City}
                                  required
                                />
                              </Form.Group>
                            </Col>
                            <Col md={6}>
                              <Form.Group>
                                <Form.Label>Τ.Κ.*</Form.Label>
                                <Form.Control
                                  type="text"
                                  value={inspection.Owner.PostalCode}
                                  onChange={e => handleInputChange("Owner.PostalCode", e.target.value)}
                                  disabled={
                                    isSubmitting.status ||
                                    inspection.Status === 99 ||
                                    inspection.Status === 10
                                  }
                                  isInvalid={!inspection.Owner.PostalCode}
                                  required
                                />
                              </Form.Group>
                            </Col>
                          </Row>
                        </Card.Body>
                      </Card>
                    </div>
                  </div>
                </div>

                {/* Vehicle Information */}
                <div className="col-12">
                  <Card>
                    <Card.Header className="bg-navy text-white">
                      <h6 className="mb-0">2. Στοιχεία του προς Ασφάλιση Οχήματος</h6>
                    </Card.Header>
                    <Card.Body>
                      <Row className="g-3">
                        <Col md={6} lg={3}>
                          <Form.Group>
                            <Form.Label>Αριθμός Κυκλοφορίας*</Form.Label>
                            <Form.Control
                              type="text"
                              value={inspection.Vehicle.PlateNumber}
                              onChange={e => handleInputChange("Vehicle.PlateNumber", e.target.value)}
                              disabled={
                                isSubmitting.status || inspection.Status === 99 || inspection.Status === 10
                              }
                              isInvalid={!inspection.Vehicle.PlateNumber}
                              required
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6} lg={3}>
                          <Form.Group>
                            <Form.Label>Ασφαλιστική Εταιρία*</Form.Label>
                            <Form.Control type="text" value={inspection.InsuranceCompany} disabled />
                          </Form.Group>
                        </Col>
                        <Col md={6} lg={3}>
                          <Form.Group>
                            <Form.Label>Εργοστάσιο Κατασκευής*</Form.Label>
                            <Form.Control
                              type="text"
                              value={inspection.Vehicle.Make}
                              onChange={e => handleInputChange("Vehicle.Make", e.target.value)}
                              disabled={
                                isSubmitting.status || inspection.Status === 99 || inspection.Status === 10
                              }
                              isInvalid={!inspection.Vehicle.Make}
                              required
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6} lg={3}>
                          <Form.Group>
                            <Form.Label>Μοντέλο*</Form.Label>
                            <Form.Control
                              type="text"
                              value={inspection.Vehicle.Model}
                              onChange={e => handleInputChange("Vehicle.Model", e.target.value)}
                              disabled={
                                isSubmitting.status || inspection.Status === 99 || inspection.Status === 10
                              }
                              isInvalid={!inspection.Vehicle.Model}
                              required
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6} lg={3}>
                          <Form.Group>
                            <Form.Label>Έτος Κατασκευής*</Form.Label>
                            <Form.Control
                              type="number"
                              value={inspection.Vehicle.ConstructionYear}
                              onChange={e => handleInputChange("Vehicle.ConstructionYear", e.target.value)}
                              disabled={
                                isSubmitting.status || inspection.Status === 99 || inspection.Status === 10
                              }
                              isInvalid={/\D/.test(inspection.Vehicle.ConstructionYear)}
                              required
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6} lg={3}>
                          <Form.Group>
                            <Form.Label>Φορολογήσιμη Ισχύς*</Form.Label>
                            <Form.Control
                              type="number"
                              value={inspection.Vehicle.HP}
                              onChange={e => handleInputChange("Vehicle.HP", e.target.value)}
                              disabled={
                                isSubmitting.status || inspection.Status === 99 || inspection.Status === 10
                              }
                              isInvalid={/\D/.test(inspection.Vehicle.HP)}
                              required
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6} lg={3}>
                          <Form.Group>
                            <Form.Label>Κυβισμός*</Form.Label>
                            <Form.Control
                              type="number"
                              value={inspection.Vehicle.CC}
                              onChange={e => handleInputChange("Vehicle.CC", e.target.value)}
                              disabled={
                                isSubmitting.status || inspection.Status === 99 || inspection.Status === 10
                              }
                              isInvalid={/\D/.test(inspection.Vehicle.CC)}
                              required
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6} lg={3}>
                          <Form.Group>
                            <Form.Label>Χρήση Οχήματος*</Form.Label>
                            <Form.Select
                              value={inspection.Vehicle.UsageCode}
                              onChange={e =>
                                handleInputChange(
                                  "Vehicle.UsageCode",
                                  e.target.value === "" ? "" : parseInt(e.target.value, 10)
                                )
                              }
                              disabled={
                                isSubmitting.status || inspection.Status === 99 || inspection.Status === 10
                              }
                              isInvalid={inspection.Vehicle.UsageCode === "" || invalidDropdowns.usage}
                              required>
                              <option value="">Επιλέξτε</option>
                              {lookupOptions.usage.map(option => (
                                <option key={option.Code} value={option.Code}>
                                  {option.Name}
                                </option>
                              ))}
                            </Form.Select>
                          </Form.Group>
                        </Col>
                        <Col md={6} lg={3}>
                          <Form.Group>
                            <Form.Label>Έτος Αγοράς</Form.Label>
                            <Form.Control
                              type="number"
                              value={inspection.Vehicle.PurchaseYear}
                              onChange={e => handleInputChange("Vehicle.PurchaseYear", e.target.value)}
                              disabled={
                                isSubmitting.status || inspection.Status === 99 || inspection.Status === 10
                              }
                              isInvalid={/\D/.test(inspection.Vehicle.PurchaseYear)}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6} lg={3}>
                          <Form.Group>
                            <Form.Label>Χρώμα Οχήματος*</Form.Label>
                            <Form.Select
                              value={inspection.Vehicle.ColorCode}
                              onChange={e =>
                                handleInputChange(
                                  "Vehicle.ColorCode",
                                  e.target.value === "" ? "" : parseInt(e.target.value, 10)
                                )
                              }
                              disabled={
                                isSubmitting.status || inspection.Status === 99 || inspection.Status === 10
                              }
                              isInvalid={inspection.Vehicle.ColorCode === "" || invalidDropdowns.color}>
                              <option value="">Επιλέξτε</option>
                              {lookupOptions.colors.map(option => (
                                <option key={option.Code} value={option.Code}>
                                  {option.Name}
                                </option>
                              ))}
                            </Form.Select>
                          </Form.Group>
                        </Col>
                        <Col md={6} lg={3}>
                          <Form.Group>
                            <Form.Label>Αξία Οχήματος*</Form.Label>
                            <Form.Control
                              type="number"
                              value={inspection.Vehicle.VehicleValue}
                              onChange={e => handleInputChange("Vehicle.VehicleValue", e.target.value)}
                              disabled={
                                isSubmitting.status || inspection.Status === 99 || inspection.Status === 10
                              }
                              isInvalid={/\D/.test(inspection.Vehicle.VehicleValue)}
                              required
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6} lg={3}>
                          <Form.Group>
                            <Form.Label>Αριθμός Θέσεων*</Form.Label>
                            <Form.Control
                              type="number"
                              value={inspection.Vehicle.Seats}
                              onChange={e => handleInputChange("Vehicle.Seats", e.target.value)}
                              disabled={
                                isSubmitting.status || inspection.Status === 99 || inspection.Status === 10
                              }
                              isInvalid={/\D/.test(inspection.Vehicle.Seats)}
                              required
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6} lg={3}>
                          <Form.Group>
                            <Form.Label>Αριθμός Πλαισίου</Form.Label>
                            <Form.Control
                              type="text"
                              value={inspection.Vehicle.ChassisNumber}
                              onChange={e => handleInputChange("Vehicle.ChassisNumber", e.target.value)}
                              disabled={
                                isSubmitting.status || inspection.Status === 99 || inspection.Status === 10
                              }
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6} lg={3}>
                          <Form.Group>
                            <Form.Label>Ένδειξη Χιλιομετρητή</Form.Label>
                            <Form.Control
                              type="number"
                              value={inspection.Vehicle.OdometerCounter}
                              onChange={e => handleInputChange("Vehicle.OdometerCounter", e.target.value)}
                              disabled={
                                isSubmitting.status || inspection.Status === 99 || inspection.Status === 10
                              }
                              isInvalid={/\D/.test(inspection.Vehicle.OdometerCounter)}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6} lg={3}>
                          <Form.Group>
                            <Form.Label>Πακέτο*</Form.Label>
                            <Form.Control
                              type="text"
                              value={inspection.Vehicle.PackageName}
                              onChange={e => handleInputChange("Vehicle.PackageName", e.target.value)}
                              disabled={
                                isSubmitting.status || inspection.Status === 99 || inspection.Status === 10
                              }
                              isInvalid={!inspection.Vehicle.PackageName}
                              required
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6} lg={3}>
                          <Form.Group>
                            <Form.Label>Απαλλαγή ΙΖ*</Form.Label>
                            <Form.Control
                              type="text"
                              value={inspection.Vehicle.DeductibleΙΖ}
                              onChange={e => handleInputChange("Vehicle.DeductibleΙΖ", e.target.value)}
                              disabled={
                                isSubmitting.status || inspection.Status === 99 || inspection.Status === 10
                              }
                              isInvalid={!inspection.Vehicle.DeductibleΙΖ}
                              required
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label>Κατάσταση Χρώματος</Form.Label>
                            <div className="d-flex gap-3">
                              {lookupOptions.conditions.map(condition => (
                                <Form.Check
                                  key={condition.Code}
                                  inline
                                  type="radio"
                                  label={condition.Name}
                                  name="paintCondition"
                                  checked={
                                    inspection.Vehicle.PaintConditionCode === condition.Code.toString()
                                  }
                                  onChange={() =>
                                    handleInputChange("Vehicle.PaintConditionCode", condition.Code.toString())
                                  }
                                  disabled={
                                    isSubmitting.status ||
                                    inspection.Status === 99 ||
                                    inspection.Status === 10
                                  }
                                />
                              ))}
                            </div>
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label>Γενική Κατάσταση Οχήματος</Form.Label>
                            <div className="d-flex gap-3">
                              {lookupOptions.conditions.map(condition => (
                                <Form.Check
                                  key={condition.Code}
                                  inline
                                  type="radio"
                                  label={condition.Name}
                                  name="generalCondition"
                                  checked={
                                    inspection.Vehicle.GeneralConditionCode === condition.Code.toString()
                                  }
                                  onChange={() =>
                                    handleInputChange(
                                      "Vehicle.GeneralConditionCode",
                                      condition.Code.toString()
                                    )
                                  }
                                  disabled={
                                    isSubmitting.status ||
                                    inspection.Status === 99 ||
                                    inspection.Status === 10
                                  }
                                />
                              ))}
                            </div>
                          </Form.Group>
                        </Col>
                        <Col xl={7} lg={6} md={5}>
                          <Form.Group>
                            <Form.Label>Αναλυτική περιγραφή προϋπαρχουσών ζημιών</Form.Label>
                            <Form.Control
                              as="textarea"
                              rows={15}
                              value={inspection.Vehicle.PreExistingDamages || ""}
                              onChange={e => handleInputChange("Vehicle.PreExistingDamages", e.target.value)}
                              disabled={
                                isSubmitting.status || inspection.Status === 99 || inspection.Status === 10
                              }
                            />
                          </Form.Group>
                        </Col>
                        <Col xl={5} lg={6} md={7}>
                          <div className="car-diagram-container position-relative" ref={carContainerRef}>
                            {inspection.Vehicle?.UsageCode >= 83 && inspection.Vehicle?.UsageCode <= 98 ? (
                              <svg
                                id="Layer_2"
                                data-name="Layer 2"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 149.99 364"
                                style={{
                                  width: "100%",
                                  height: "70%",
                                  transform: "translate(2%, 23%)"
                                }}>
                                <g id="Layer_2-2" data-name="Layer 2">
                                  <g id="Layer_1-2" data-name="Layer 1-2">
                                    <g>
                                      <path
                                        className="cls-1"
                                        d="m135.14,135.38c7.49,2.81,10.77,9.28,14.25,15.54.36.65.95,1.46.33,2.18-.62.73-1.48.2-2.21,0-2.81-.81-5.62-1.66-8.42-2.51-1.1-.33-2.18-.67-3.35-.26-.37.13-.91.15-1.25-.02-4.94-2.57-10.36-1.15-15.51-1.89-1-.14-1.35.46-1.24,1.48.82,7.67,1.62,15.34,2.41,23.01.7,6.82,1.48,13.63,2.05,20.47.6,7.26,1.77,14.46,1.82,21.77.04,5.26-.5,10.42-2.07,15.44-.69,2.22-.86,4.39-.65,6.73.45,5.21-.11,10.44-.51,15.63-.86,11.19-2.4,22.31-4.98,33.24-2.16,9.19-4.9,18.18-9.1,26.72-3.1,6.29-6.84,12.03-11.92,16.88-.99.94-1.34,2.17-1.55,3.46-1.41,8.63-3.5,17.08-6.3,25.36-.91,2.68-2.16,4.61-5.48,4.73-4.48.16-8.92,1.24-13.42.24-1.58-.35-2.62-1.12-3.32-2.67-2.2-4.86-3.22-10.07-4.62-15.16-1.24-4.47-2.14-9.04-3.14-13.58-.19-.86-.74-1.33-1.32-1.87-6.8-6.39-11.39-14.2-14.88-22.78-3.16-7.77-5.48-15.78-7.29-23.95-2.28-10.27-3.48-20.7-4.4-31.17-.56-6.35-.43-12.68-.2-19.05.12-3.25-1.3-6.54-1.88-9.85-.84-4.78-.67-9.56-.28-14.32.66-8.11,1.51-16.21,2.31-24.31.61-6.19,1.31-12.36,1.88-18.55.47-5.2,1.11-10.37,1.86-15.53.23-1.55-.13-2.5-1.99-2.36-4.41.34-8.95.09-13.22,1.02-4.98,1.09-10.16,1.52-14.92,3.62-.7.31-1.73.9-2.39.06-.61-.78.11-1.63.48-2.37,2.24-4.51,4.91-8.7,8.75-12.06,1.58-1.38,3.57-1.99,5.45-2.93-.52-.73-1.12-.19-1.63-.34-1.4-.43-2.07-1.46-1.73-2.76.37-1.4,1.62-1.9,2.91-1.21,3.78,2.02,7.92,1.44,11.93,1.73,1.26.09,2.35-.5,3.17-1.52.68-.85,1.42-1.61,2.59-1.76.26-.03.74-.27.73-.35-.37-2.4,2.27-3.44,2.54-5.37.53-3.84,1.48-7.67-.02-11.61-2.42-6.36-3.49-13.1-5.18-19.66-.97-3.78-.91-7.25.53-10.78,1.94-4.74,2.31-10.08,5.24-14.43,2.6-3.86,5.71-7.32,9.35-10.26.78-.63,1.28-1.28,1.27-2.32-.04-2.57.06-5.16-.08-7.72-.1-1.8-1.67-3.21-1.32-5.25.44-2.52,1.75-4.31,3.87-5.52,1.2-.68,2.67-.6,3.66.29,1.7,1.54,2.25,1.05,2.88-.92,1.59-5.04,3.57-9.96,5.04-15.03.88-3.01.39-6.25.28-9.36-.07-2.28.39-4.53.02-6.83-.22-1.39.97-2.25,2.28-2.75,7.92-3,15.79-2.49,23.66.21,1.6.55,2.21,1.65,2.21,3.26-.01,3.72.06,7.44-.02,11.16-.04,2.11.24,4.16,1.04,6.09,1.92,4.63,3.21,9.47,4.82,14.21.36,1.07.64,1.4,1.74.6,3.41-2.5,7.93-.33,8.28,3.97.12,1.52.33,3.08-.86,4.48-.76.89-.27,2.22-.4,3.34-.56,4.72.23,8.57,4.34,11.97,4.53,3.74,7.53,8.84,8.72,14.89.7,3.55,2.13,6.96,3.25,10.43.18.56.3,1.09.16,1.67-1.97,7.97-3.34,16.08-5.99,23.88-1.07,3.14.12,6.23.2,9.34.05,1.91.22,3.87,1.91,5.26.46.38.44,1.03.46,1.61.02.51.03,1.22.58,1.25,2.64.16,3.34,3.5,6.14,3.53,2.37.03,4.71-.27,7.06-.4,1.46-.08,3.01-.14,4.3-1.1.95-.71,2.01-.84,2.95,0,.63.57.78,1.35.47,2.17-.44,1.18-1.29,1.65-2.54,1.41-.18-.03-.38.08-.57.12h-.02Zm-95.26,115.05c-.11,5.93.95,11.19,1.69,16.49,1.03,7.41,2.69,14.63,6.22,21.28,4.46,8.39,10.77,14.53,20.56,16.24,8.19,1.43,15.87.2,22.57-4.88,7.15-5.42,10.93-13.11,13.17-21.55,1.68-6.34,2.76-12.83,3.44-19.37.55-5.28.91-10.52-.25-15.78-.31-1.4-.83-1.63-2.01-1.19-.6.22-1.23.37-1.83.6-4.44,1.69-9.14,2.28-13.78,3.03-6.38,1.02-12.84.52-19.27.49-2.56,0-5.13-.33-7.66-.72-6.7-1.04-13.38-2.27-19.9-4.18-1.27-.37-1.77.15-2.07,1.38-.69,2.87-1.03,5.76-.88,8.17h0Zm-9.84-19.18c2.01-3.64,3.76-7.07,6.01-10.19,1.16-1.6,1.73-3.4,2.01-5.36.33-2.26.71-4.53,1.32-6.73,3.72-13.51,9.77-25.87,18.16-37.11,7.47-10.01,20.94-10.07,28.48-4.27,3.04,2.33,5.22,5.41,7.37,8.56,7.51,11.07,12.95,23,15.75,36.12.33,1.53.85,3,1.73,4.36,2.77,4.25,5.66,8.42,8.01,12.93.3.58.28,1.69,1.16,1.55.74-.12.68-1.14.86-1.81,1.9-7.15,2.06-14.38,1.28-21.7-.96-8.95-1.7-17.92-2.61-26.87-.81-8.03-1.73-16.05-2.6-24.07-.13-1.21.09-2.63-.49-3.57-.69-1.12-2.05,0-3.12,0-25.88.02-51.77.49-77.61-1.35-1.57-.11-1.89.41-2.03,1.84-.72,7.32-1.6,14.62-2.36,21.93-.65,6.23-1.23,12.48-1.83,18.72-.41,4.26-.85,8.53-1.2,12.8-.49,5.92-.63,11.83.25,17.74.31,2.11.75,4.16,1.45,6.48h0Zm48.24,13.65c8.4.2,17.44-1.25,26.27-4.13,1.66-.54,2.13-1.07,1.35-2.84-4.92-11.05-8.27-22.66-11.88-34.18-2.33-7.42-4.29-15-7.87-21.97-1.6-3.12-3.23-6.35-6.84-7.63-3.7-1.31-7.49-.73-11.16.2-2.15.54-3.45,2.43-4.63,4.26-3.76,5.85-5.64,12.47-7.74,18.99-3.23,10.05-5.93,20.26-9.68,30.14-1.24,3.27-2.63,6.49-3.94,9.73-.46,1.13-.57,1.91,1.03,2.35,11.2,3.08,22.52,5.23,35.09,5.08Zm-41.51-21.46c-.18-.04-.37-.07-.55-.11-1.42,2.47-2.84,4.95-4.27,7.41-1.71,2.94-2.06,6.11-2.04,9.47.04,6.16.7,12.25,1.25,18.38,1.25,13.86,3.81,27.42,8.1,40.67,3.07,9.47,7.05,18.44,13.54,26.08,5.82,6.84,13.08,11.07,22.29,10.99,10.56-.08,18.24-5.61,24.2-13.75,8.5-11.62,12.49-25.12,15.59-38.93,2.13-9.49,3.35-19.14,4.28-28.82.58-5.98,1.41-12.02.7-17.97-.79-6.63-5.33-11.62-8.84-17.56-.63,2.3.04,3.95.08,5.63.45,19.42-.29,38.74-5.14,57.68-1.44,5.64-3.3,11.16-6.93,15.79-4.55,5.79-10.79,9.21-17.57,11.79-3.84,1.46-7.71,2.14-11.74,1.07-4.02-1.07-7.9-2.51-11.44-4.75-4.63-2.94-8.73-6.35-11.35-11.38-4.17-7.99-6.11-16.64-7.5-25.39-2.17-13.72-3.09-27.55-2.68-41.45.05-1.62,0-3.25,0-4.87l.02.02Zm37.18-128.72h.17v-4.51c-.02-2.72-.42-3.05-3.2-2.7-4.09.52-7.87,2.05-11.69,3.46-2.11.78-2.42.67-2.31-1.54.27-5.78.67-11.56.94-17.34.11-2.38,1.14-4.25,2.82-5.8,3.54-3.24,7.22-6.3,11.42-8.65,1.03-.58,1.98-1.15,1.98-2.54-.02-3.29,0-6.58,0-9.87,0-1.01-.32-1.36-1.46-1.03-6.71,1.93-13.03,4.67-18.84,8.6-1.21.82-1.91,1.57-1.61,3.09s-.22,2.69-1.51,3.71c-4.61,3.66-8.61,7.91-12.18,12.59-1.27,1.67-2.01,3.41-1.59,5.56.93,4.77,1.25,9.62,1.9,14.43,1.1,8.2,1.69,16.47,1.66,24.77,0,.64.03,1.37.66,1.71.71.38,1.2-.23,1.68-.62,2.06-1.65,4.07-3.38,6.18-4.96,5.81-4.33,11.92-8.13,18.92-10.32,2-.62,4.9.12,5.86-1.72.88-1.68.18-4.18.18-6.32h.02Zm1.23.2h-.08c0,1.79.02,3.57,0,5.36-.01,1.04.41,1.54,1.51,1.61,7.01.47,12.97,3.63,18.76,7.28,4.12,2.6,7.98,5.52,11.65,8.72.37.32.75.74,1.31.52.5-.2.43-.68.44-1.11.1-4.64.63-9.26.84-13.88.38-8.58,1.74-17.03,2.85-25.51.26-2.01-.02-3.59-1.21-5.19-3.2-4.33-6.77-8.3-10.91-11.75-.8-.67-1.56-1.4-1.22-2.53.74-2.4-.53-3.65-2.36-4.87-6.16-4.15-12.81-7.18-19.88-9.4-1.47-.46-1.69.33-1.71,1.32-.06,3-.06,6-.01,9,.02,1.15.62,2.07,1.68,2.57,3.59,1.71,6.55,4.32,9.67,6.7,3.53,2.7,6.4,5.75,5.73,10.72-.07.48.13.99.15,1.49.23,5.21.45,10.42.66,15.63.07,1.76-.76,1.95-2.13,1.21-.57-.3-1.15-.57-1.73-.85-3.59-1.78-7.29-3.29-11.19-4.25-2.22-.55-2.74-.14-2.81,2.08-.05,1.71,0,3.43,0,5.14h-.01Zm-36.41,153.75c0-3.25.78-6.34,1.68-9.43,1.39-4.75,3.85-9.08,5.35-13.77,2.58-8.09,5.41-16.12,7.63-24.31,2.12-7.83,5.97-15.03,7.87-22.91.03-.12.13-.33.18-.32,1.79.22,2.16-1.48,2.96-2.39,1.85-2.1,4.16-2.95,6.8-3.14,1.64-.12,3.29-.05,4.93-.03,3.45.05,6.12,1.48,8.21,4.27,2.17,2.9,3.74,6.12,4.98,9.45,3.35,8.97,5.99,18.18,8.85,27.32,1.98,6.34,4.02,12.66,6.52,18.82,1.07,2.64,2.35,5.19,3.11,7.97,1.23,4.47,1.32,9.02,1.28,13.61.27-4.63.6-9.25.69-13.88.23-12.05-.3-23.95-4.2-35.56-3.74-11.15-8.85-21.51-15.91-30.91-3.12-4.16-7-7.17-12.26-7.96-5.59-.84-10.79.03-15.26,3.81-2.87,2.42-5.03,5.4-7.1,8.49-5.96,8.9-10.39,18.51-13.47,28.77-4.14,13.82-3.84,27.93-2.82,42.11h-.02Zm14.21-197.32c.31-.1.6-.14.82-.28,5.52-3.53,11.51-6.01,17.68-8.12,1.9-.65,3.72-.85,5.65-.22,6.12,2.02,12.11,4.32,17.56,7.83.52.33,1.05,1.05,1.68.68.73-.43.31-1.29.28-1.97-.01-.35-.16-.69-.23-1.04-1.22-6.18-3.14-12.19-5.24-18.1-1.13-3.18-1.12-7.07-4.51-9.19-.04-.02.08-.25.08-.39.03-4.57-3.64-8.97-8.09-9.83-1.53-.3-3.34.36-4.35.08-2.25-.63-3.75.24-5.21,1.24-3.12,2.13-5.29,4.99-6.67,8.64-3.46,9.16-6.24,18.54-9.13,27.88-.26.84-.65,1.74-.32,2.79Zm5.58,31.54c0,1.36.09,2.72-.02,4.07-.23,2.94-.1,3.16,2.54,1.91,9.85-4.66,19.32-3.17,28.57,1.66,1.66.87,2.11.69,2-1.16-.35-6.13-.65-12.26-.95-18.39-.04-.85-.41-1.47-.95-2.08-3.79-4.26-8.37-7.51-13.18-10.49-1.28-.8-2.43-.53-3.6.22-3.06,1.97-6.14,3.96-8.83,6.41-2.2,2.01-4.95,3.73-4.93,7.37.02,3.49-.71,6.96-.66,10.48h0Zm38.7,51.04c-1.69-11.87-7.15-20.85-17.06-26.83-3.53-2.13-7.41-1.75-10.99.52-5.63,3.58-9.82,8.51-12.87,14.34-1.9,3.63-3.36,7.51-3.43,11.92,14.74-9.36,29.34-9.36,44.35.04h0Zm-50.87,3.01c.56-.11,1.1-.27,1.64-.32,2.37-.22,3.56-1.46,3.57-3.89,0-.99.12-2.01.36-2.97,2.48-10.1,7.99-18.09,16.85-23.6,4.28-2.66,8.87-2.55,13,.19,9.26,6.15,15.06,14.66,16.51,25.86.48,3.72,1.43,4.54,5.42,4.42-.37-.35-.68-.63-.99-.92-1.92-1.83-2.49-4.14-1.48-5.98,1.11-2,3.75-3.22,6.06-2.7,2,.45,3.72,1.42,5.2,2.86.62.61,1,1.93,2.11,1.42,1.1-.5.56-1.78.62-2.73.07-.94-.53-1.6-1.15-2.21-7.53-7.38-15.5-14.16-24.84-19.22-9.52-5.16-18.98-5.09-28.47-.03-8.65,4.62-16.13,10.8-23.13,17.57-1.38,1.33-3.53,2.47-3.01,5.06.12.61-.11,1.39.69,1.64.75.23,1.18-.33,1.57-.86,1.16-1.55,2.78-2.46,4.51-3.17,2.59-1.08,5.68-.09,7.21,2.22,1.22,1.85.63,4.18-1.56,6.28-.29.28-.77.42-.69,1.09h0Zm12.34,196.09c2.16,9.28,3.77,18.57,7.29,27.36.45,1.14,1.26,1.58,2.41,1.82,3.86.79,7.69.06,11.52-.11,3.81-.17,4.65-.77,5.88-4.36,2.17-6.36,3.5-12.94,5.07-19.46.45-1.85,1.02-3.7.97-5.99-5.1,3.37-10.41,5.41-16.36,5.48-6.01.07-11.53-1.56-16.78-4.73h0Zm-12.26-194.53c-2.76,1.14-5.01,2.27-7.37,3.01-3.68,1.14-4.41,3.65-3.99,7.07.17,1.36.61,1.84,1.95,1.89,10.86.4,21.7.98,32.56,1.24,8.51.2,17.02.06,25.54,0,5.86-.05,11.72-.22,17.58-.37.74-.02,2.11.32,1.81-1.05-.4-1.88-.05-3.7-.11-5.55-.02-.75-.14-1.43-.85-1.84-3.07-1.79-6.4-2.93-9.92-4.16.11.56.14.84.23,1.1,1.31,4.04.5,5.39-3.65,6.02-3.89.59-7.81.85-11.75.86-8.94,0-17.88,0-26.83,0-4.01,0-8-.24-11.96-.85-3.77-.58-4.72-2.12-3.63-5.82.1-.33.17-.66.38-1.53v-.03Zm.69,3.72c.35,1.71,1.3,2.06,2.41,2.25,3.96.67,7.98.67,11.96.75,9,.18,18.01.19,27.02,0,4.05-.09,8.14-.09,12.17-.8,1.83-.32,2.46-1.41,2.07-3.06-.45-1.9-1.08-3.77-3.52-4-1.04-.1-1.81-.69-2.57-1.4-2.82-2.61-6.12-4.45-9.78-5.59-6.16-1.92-12.42-2.42-18.82-1.27-5.25.94-10.03,2.86-14.12,6.28-1.23,1.03-2.42,1.95-4.03,2.24-.86.15-1.58.53-1.67,1.59,1.69.33,3.67-.69,4.74,1.3.32.6.18,1.32-.35,1.78-1.66,1.47-3.45.87-5.51-.06h0ZM87.69,15.12v-6.33c0-4.85-.06-4.67-4.53-6.19-6.39-2.18-12.62-1.65-18.86.47-1.02.35-1.83.79-1.83,2.04,0,3.6,0,7.19,0,10.79.17.09.34.18.51.27,2.77-4.5,6.87-7.19,11.87-7.63,5.25-.46,9.11,2.71,12.83,6.58h.01Zm-44.61,264.66c-.04,2.3,1.01,4.3,1.67,6.39,2.1,6.64,5.08,12.75,11.03,16.92,2.47,1.73,5.05,3.24,7.74,4.57,6.62,3.26,13.29,3.47,20.01.32,1.36-.64,2.72-1.3,3.99-2.09,3.34-2.06,6.76-4.03,9.37-7.05,3.85-4.44,5.66-9.87,7.36-15.34.33-1.07.79-2.17.55-3.4-5.11,15.45-14.32,26.48-31.77,26.02-16.71-.43-25.16-11.61-29.96-26.34h.01Zm104.68-128.16c-2.88-9-11.36-15.73-20.12-16.4-.83-.06-1.37.1-1.65.95-.25.75-.6,1.45-.85,2.2-.69,2.01.76,6.74,2.39,7.24,6.6,2.03,13.24,3.95,20.24,6.01h0Zm-145.37-.39c.54.35,1,0,1.45-.12,5.29-1.5,10.51-3.27,15.87-4.45,3.45-.76,4.58-2.97,5.24-5.87.43-1.88-.39-3.62-1.33-5.05-.85-1.29-2.46-.31-3.56,0-4.11,1.12-8.38,2.1-11.38,5.5-2.08,2.36-3.91,4.92-5.49,7.64-.42.72-.95,1.43-.8,2.34h0Zm111.82-77.88c-2.08,14.19-4.17,28.43-4.04,43.45,1.84-2.06,2.68-3.71,2.02-5.91-.55-1.85-.9-3.82-.68-5.71.61-5.17,1.52-10.29,2.64-15.38,1.19-5.44,3.29-10.92.06-16.46h0Zm-11.93-26.63h.06v-6.21c0-1.68-.79-2.76-2.54-2.81-1.69-.04-2.66.96-2.9,2.57-.21,1.42-.05,2.83.25,4.25.44,2.09.87,4.21.99,6.33.09,1.54.68,2.53,1.9,3.33,1.62,1.06,2.15.87,2.22-1.03.08-2.14.02-4.29.02-6.43Zm-67.02,26.65c-2.44,4.43-2.34,8.57-.85,12.93,1.31,3.84,2.34,7.8,3.05,11.82.82,4.56,1.03,9.09-.23,13.6-.56,1.99.39,3.36,2.06,4.98.15-14.81-2.27-28.92-4.03-43.34h0Zm12.68-26.55h.04c0,2.14,0,4.28.02,6.42,0,.46-.23,1.11.35,1.33.5.19.9-.32,1.29-.6.29-.2.65-.44.76-.74,1.44-4.15,2.83-8.33,3.26-12.71.14-1.45-1.88-2.95-3.25-2.79-1.82.21-2.53,1.33-2.48,3.11.05,1.99,0,3.99.02,5.99h0Zm-11.66,62.86c1.72-9.27-1.93-17.33-4.14-25.64-1.09,1.86-1.47,3.65-1.07,5.76,1.13,6.11,2.89,12.06,4.13,18.13.11.55.27,1.1,1.08,1.74h0Zm81-26.54c-.24.33-.43.47-.47.65-1.38,7.07-3.07,14.09-4,21.24-.17,1.33.03,2.62.69,4.25.41-1.23.77-2.08.97-2.97,1.24-5.28,2.38-10.59,3.7-15.85.64-2.57.18-4.87-.89-7.33h0Zm-80.71-10.87c.41-.43.73-.7.96-1.03,2.57-3.62,5.56-6.87,8.82-9.87,1.21-1.11,2.5-2.13,3.75-3.19.71-.61.81-1.38.34-2.12-.51-.8-1.05-.11-1.43.19-4.57,3.6-8.68,7.65-12.03,12.43-.73,1.04-.89,2.18-.41,3.59Zm64.47-15.35c-.8,1.48-.52,2.47.73,3.41,1.58,1.2,2.95,2.65,4.33,4.09,2.33,2.46,4.43,5.12,6.67,7.85.69-3.31-4.37-9.84-11.73-15.35Zm25.94,90.21c-1.3-.34-1.87-1.21-2.2-2.39-.2-.72-.36-1.67-1.28-1.82-1.68-.26-3.39-.58-5.07-.49-1.99.1-1.13,1.93-1.43,3.03-.21.79-.04,1.46.97,1.45,2.98-.05,5.92.78,9,.23h0Zm-104.24.21c3.18.37,6.05-.29,8.95-.44,1.64-.09,1.08-1.54,1.13-2.53.06-1.1-.19-2.13-1.59-1.95-2.51.32-5.65-.52-6.42,3.27-.19.93-1.3.94-2.07,1.64h0Zm16.53-12.86c1.43,0,2.61.2,3.69-.05,1.25-.3,2.58-1.14,2.39-2.57-.2-1.49-1.7-1.58-2.93-1.37-2.55.44-4.65,2.07-7.09,2.83-.4.13-.63.6-.45,1.07.19.53.65.43,1.08.39,1.2-.12,2.4-.22,3.31-.3Zm68.28-4.04c-1.32-.05-2.3-.07-2.73.98-.47,1.16.33,1.85,1.09,2.52.51.45,1.13.56,1.78.57,2.12.06,4.26-.2,6.37.23.45.09.89.04,1.01-.49.1-.47-.15-.9-.58-1.03-2.51-.75-4.67-2.41-6.95-2.79h.01Zm-64.23,7.7c-2.74.03-3.32-.07-5.67-1.55-.93-.59-1.71-.71-2.37.28-.46.69-.24,4.34.25,4.48.24.07.56-.02.81-.12,2.33-1.02,4.66-2.06,6.99-3.1h-.01Zm74.76-4.16c-.59-.06-1.19-.12-1.36.64-.16.74.2,1.25.86,1.53,1.63.69,3.29,1.35,4.92,2.05.56.24.88.12,1.23-.39,1.36-2.03.94-2.99-1.42-3.24-1.41-.15-2.81-.39-4.22-.6h-.01Zm-84.86,1c0-.88-.53-1.06-1.28-.96-1.55.21-3.1.41-4.64.64-.65.1-1.62-.27-1.77.71-.14.92.15,2.27.8,2.52,1.97.77,3.48-1.03,5.29-1.45.75-.17,1.53-.47,1.6-1.47h0Zm74.53,3.44c2.02.97,4.03,1.8,5.95,2.82.54.28,1.01.16,1.04-.54.05-1.13.17-2.29,0-3.39-.17-1.13-1.13-1.66-2.04-.97-1.48,1.13-3.2,1.43-4.96,2.09h.01Zm-74.32-.93c-2.09.44-4.76,1.55-5.93,2.4-.34.25-.83.43-.73.94.1.53.62.54,1.04.5,1.48-.15,3.01-.17,4.41-.6,1-.31.14-1.76.87-2.46.13-.13.17-.36.34-.78h0Z"
                                      />
                                      <path
                                        className="cls-1"
                                        d="m75.52,341.54h4.28c1.24,0,1.85.58,1.81,1.83,0,.21,0,.43.01.64.09,3.97-.04,4.04-4.04,3.78-2.13-.14-4.28-.12-6.42-.08-1.48.03-2.1-.61-2.05-2.04.02-.57.03-1.14-.01-1.71-.12-1.7.58-2.54,2.35-2.45,1.35.07,2.71.01,4.07.01h0v.02Zm.28,1.69h-3.41c-1.01.01-1.6.47-1.58,1.56.02.97.53,1.44,1.46,1.45,2.27.02,4.54.01,6.81,0,.94,0,1.42-.46,1.44-1.44.03-1.09-.5-1.56-1.53-1.57h-3.19Z"
                                      />
                                      <path
                                        className="cls-1"
                                        d="m65.23,141.98c-2.23-.14-4.27-.49-6.16-1.41-1.51-.74-2.52-1.92-2.57-3.67-.05-1.7.93-2.77,2.38-3.51,4.55-2.32,9.08-2.4,13.46.31,2.68,1.66,2.51,5.02-.23,6.63-2.17,1.27-4.58,1.53-6.89,1.65h.01Zm.18-8.63c-2.28-.14-4.5.17-6.39,1.7-1.44,1.17-1.43,2.59,0,3.74,2.69,2.17,9.91,2.24,12.6.12,1.58-1.24,1.55-2.81-.08-4.06-1.8-1.38-3.9-1.61-6.13-1.5Z"
                                      />
                                      <path
                                        className="cls-1"
                                        d="m83.7,141.95c-1.97-.18-4.25-.39-6.25-1.66-2.85-1.82-2.86-4.86-.04-6.75,3.7-2.48,10.12-2.42,13.81.12,2.67,1.84,2.48,5.19-.39,6.77-1.67.92-4.43,1.54-7.13,1.52Zm.44-8.6c-2.04-.11-4.05.11-5.86,1.35-1.97,1.35-2.1,3.27-.02,4.38,3.91,2.08,7.98,2.04,11.9.02,2.06-1.06,1.94-3.08-.03-4.39-1.81-1.2-3.82-1.5-5.99-1.36h0Z"
                                      />
                                      <path
                                        className="cls-1"
                                        d="m97.74,139.19c1.37.1,2.92.17,2.86,1.88-.05,1.66-1.56,2.11-2.97,2.06s-3.2-.28-3.13-2.11c.07-1.89,1.91-1.7,3.24-1.83Z"
                                      />
                                    </g>
                                  </g>
                                </g>
                              </svg>
                            ) : (
                              <svg
                                id="Layer_2"
                                data-name="Layer 2"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 3320.58 5598.78"
                                style={{
                                  width: "100%",
                                  height: "70%",
                                  transform: "translate(2%, 23%)"
                                }}>
                                <g id="Layer_1-2" data-name="Layer 1">
                                  <path
                                    className="cls-1"
                                    d="m2368.18,2656.97c7.2,2.58,16.13,2.5,25.6.33,11.82-2.7,23.77-8.64,34.26-16.9,30.8-24.27,60.37-67.79,88.65-122.62,2.52-5.38,5.07-10.72,7.68-15.98l.3.15c52.19-105.9,99.92-248.42,142.81-376.55,6.36-18.99,12.61-37.68,18.77-55.9,2.62-7.76,5.22-15.2,7.92-22.97,26.16-75.05,64.6-185.33,8.78-233.88-242.2-210.65-651.29-315.98-1061.65-315.98s-819.44,105.33-1061.65,315.98c-55.82,48.55-17.38,158.83,8.78,233.88,2.71,7.77,5.3,15.21,7.92,22.97,6.16,18.22,12.41,36.91,18.77,55.9,42.94,128.25,90.71,270.92,142.97,376.86l.93-.46c4.57,9.23,9.01,18.67,13.36,28.28,26.32,49.03,53.73,87.89,82.19,110.32,10.49,8.26,22.44,14.2,34.26,16.9,9.46,2.16,18.4,2.25,25.6-.33,220.61-79.1,474.03-118.65,726.87-118.65s506.25,39.55,726.87,118.65m-445.39,1812.28c14.96-1.77,28.53,8.93,30.29,23.89s-8.93,28.53-23.89,30.29c-94.17,11.32-190.31,16.61-286.75,15.86-96.23-.75-192.17-7.53-286.15-20.36-14.9-2-25.36-15.71-23.36-30.61s15.71-25.36,30.61-23.36c92.05,12.56,185.63,19.21,279.11,19.93,93.41.72,187.33-4.49,280.13-15.65m-1081.12-1735.98c57.79,228.05,75.21,509.92,59.21,791.4-13.17,231.75-49.03,464.03-103.65,666.41,44.73-38.29,90.89-60.69,136.53-60.74,16.07-.02,31.94,2.65,47.52,8.23,60.04,21.53,122.21,39.95,185.88,55.26,64.47,15.51,130.88,27.88,198.56,37.12,91.47,12.48,184.34,19.09,276.99,19.8,92.51.72,185.76-4.48,278.15-15.59,73.59-8.85,145.93-21.41,216.16-37.67,69.72-16.14,137.25-35.79,201.79-58.93,15.58-5.59,31.45-8.25,47.52-8.24,43.36.05,87.19,20.27,129.81,55.12-59.28-206.63-100.72-444.21-118.23-680.39-20.4-275.23-8.35-549.76,45.69-772.94-9.88,4.38-20.09,7.81-30.38,10.17-24.9,5.7-50.65,5.12-74.41-3.4-210.46-75.46-453.69-113.19-697.51-113.19s-487.05,37.73-697.51,113.19c-23.76,8.52-49.51,9.1-74.41,3.4-9.37-2.14-18.67-5.19-27.72-9.02m-130.97,1714.28c-21.29,50.56-44.12,97.16-68.37,139-14.76,34.08-28.47,68.79-40.87,103.36-37.44,104.41-62.65,206.61-68.35,285.22v14.69h-.17c1.86,208.11,71.81,331.88,168.61,406.21,97.83,75.12,225.23,102.61,338.7,115.37h1240.97c113.47-12.76,240.87-40.24,338.7-115.37,94.14-72.29,162.88-191.36,168.29-389.29-.1-81.61-26.89-197.78-69.58-316.83-12.84-35.8-27.08-71.74-42.43-106.98-24.69-42.57-48.18-90.07-70.32-141.63-17.03-30.04-34.71-58.38-52.78-84.27-58.19-83.39-117.57-139.6-166.77-139.65-6.2,0-12.27,1-18.16,3.12-68.66,24.62-139.49,45.29-211.69,62-73.85,17.1-149.26,30.23-225.38,39.38-94.59,11.37-191.39,16.68-288.73,15.93-97.05-.75-193.7-7.58-288.26-20.49-69.88-9.54-139.13-22.48-207.09-38.82-66.88-16.08-132.13-35.42-195.1-58-5.89-2.11-11.96-3.12-18.16-3.11-49.2.06-108.58,56.26-166.77,139.65-19.32,27.68-38.18,58.15-56.29,90.52m-78.6-37.64c62.29-148.63,110.94-333.8,143.03-532.54-58.97,54.23-111.77,116.46-153.12,179.93-51.34,78.8-84.54,158.33-88.9,224.74v341.95c28.32-73.88,62.07-147.46,98.99-214.08m161.23-660.93c9.25-75.56,16.15-152.31,20.51-229.08,20.76-365.15-15.54-728.57-124.98-962.88-59.4-114.77-110.15-266.09-155.74-402.26v1880.34c5.14-8.51,10.42-16.92,15.85-25.25,62.68-96.21,149.26-189.72,244.37-260.88M444.82,1396.88c0-3.06.32-6.05.92-8.94v-704.16c0-240.34,47.83-405.48,158.31-515.06C714.02,59.64,882.2,10.04,1123.51.17l1.71-.17h1071.05l1.71.17c241.31,9.87,409.49,59.47,519.46,168.55,110.48,109.59,158.31,274.72,158.31,515.06v713.09c0,75.08,7.13,128.55,22.82,171.88,15.36,42.43,39.64,77.08,74.09,114.69l.08-.08,269.52,292.99c5.66,6.15,10.17,10.87,14.57,15.47,53.16,55.6,94.02,98.34,33.98,195.02-2.74,4.41-5.84,8.93-9.28,13.55-28.28,38-62.16,53.94-97.95,55.33-32.64,1.27-64.51-10.49-93.57-28.54-6.36-3.95-13-8.53-19.81-13.6l-194.45-144.7v2920.93l-.17.34c-2.06,240.2-86.22,385.68-202.75,475.16-114.68,88.06-258.49,119.14-385.09,133.11l-3.09.34h-1247.84l-3.09-.34c-126.59-13.97-270.41-45.05-385.09-133.11-113.67-87.29-196.53-227.89-202.43-457.7h-1.37c-.01-9.48.29-19.34.89-29.54v-681.22h-.15c0-3.16.05-6.34.15-9.54v-2219.12l-195.36,145.38c-6.81,5.07-13.46,9.66-19.81,13.6-29.06,18.05-60.93,29.81-93.57,28.54-35.79-1.4-69.67-17.33-97.95-55.33-3.44-4.62-6.54-9.14-9.28-13.55-60.05-96.69-19.19-139.42,33.97-195.02,4.39-4.59,8.91-9.31,14.57-15.47l269.52-292.99.08.08c34.45-37.61,58.73-72.26,74.09-114.69,15.69-43.33,22.82-96.8,22.82-171.88m.92,305.59c-10.25,13.26-21.48,26.4-33.74,39.78v.17l-269.52,292.99c-4.51,4.91-10.13,10.78-15.59,16.49-26.82,28.05-47.43,49.61-23.05,88.87,1.98,3.19,3.71,5.77,5.18,7.75,10.41,13.99,20.94,19.78,31.05,20.18,13.26.52,28.8-5.87,44.42-15.56,5.12-3.18,9.78-6.36,14.01-9.51l247.24-183.99v-257.16Zm2430.02,257.85l246.33,183.3c4.23,3.15,8.89,6.33,14.01,9.51,15.62,9.7,31.15,16.08,44.42,15.56,10.11-.39,20.64-6.19,31.05-20.18,1.47-1.98,3.2-4.56,5.18-7.75,24.38-39.26,3.77-60.82-23.05-88.87-5.46-5.71-11.08-11.58-15.59-16.49l-269.52-292.99v-.17c-11.9-12.98-22.82-25.74-32.83-38.6v256.66Zm-87.38,2328.22c-2.72-68.02-38.96-152.88-94.77-237.05-44.48-67.09-100.91-132.76-162.64-188.75,36.23,201.47,88.82,389.57,153.37,540.57,38.96,69.42,74.51,146.75,104.04,224.29v-339.06Zm-278.16-555.15c97.77,71.65,189.47,169.48,256.09,269.97,7.69,11.6,15.05,23.25,22.06,34.93v-1997.86c-4.02,12.08-8.08,23.73-11.93,34.78-2.76,7.92-5.4,15.5-7.58,21.95-6.16,18.22-12.41,36.91-18.77,55.9-43.55,130.08-91.81,274.21-147.82,387.19l.26.13c-113.54,229.21-144.75,591.89-117.57,958.55,5.82,78.5,14.32,157.07,25.25,234.47m278.16-1955.59V683.79c0-216.15-39.99-361.65-132.37-453.28-20.32-20.15-43.36-37.92-69.24-53.47v1448.8c63.75,35.86,122.06,76.17,173.54,120.94,11.14,9.69,20.41,20.07,28.07,31.01m-288.99-196.8v-933.6l-293.91-210.3c-21.71-15.53-40.71-41.12-54.04-68.96-13.53-28.26-21.97-60.09-21.97-86.78V87.38h-937.44v193.98c0,26.69-8.44,58.52-21.97,86.78-13.33,27.85-32.33,53.43-54.04,68.96l-293.91,210.3v916.12c237.95-102.81,528.86-154.23,819.21-154.23,306.35,0,613.33,57.24,858.06,171.71m-1764.65,23.87V177.04c-25.89,15.55-48.92,33.31-69.24,53.47-92.38,91.63-132.37,237.14-132.37,453.28v1053.78c59.27-49.93,127.13-94.16,201.61-132.69M1104.64,88.26c-112.57,5.92-206.15,21.19-282.53,48.23v403.77l243.39-174.15c9.34-6.68,18.62-20,26.05-35.51,8.07-16.84,13.09-34.95,13.09-49.23V88.26Zm1394.74,452V136.49c-76.38-27.04-169.96-42.31-282.53-48.23v193.1c0,14.29,5.03,32.39,13.09,49.23,7.42,15.51,16.71,28.83,26.05,35.51l243.39,174.15Z"
                                  />
                                </g>
                              </svg>
                            )}

                            {/* Checkboxes positioned around the car */}
                            {containerSize.width > 0 &&
                              [...Array(11)].map((_, index) => {
                                const position = getDamageMarkerStyle(index)
                                return (
                                  <div
                                    key={index}
                                    className="position-absolute translate-middle d-flex align-items-center gap-1"
                                    style={position}>
                                    <Form.Check
                                      width={10}
                                      height={10}
                                      type="checkbox"
                                      id={`damage-${index + 1}`}
                                      checked={inspection.Vehicle.PreExistingDamagesList?.short?.includes(
                                        index + 1
                                      )}
                                      onChange={() =>
                                        handleArrayFieldChange("Vehicle.PreExistingDamagesList", index + 1)
                                      }
                                      disabled={
                                        isSubmitting.status ||
                                        inspection.Status === 99 ||
                                        inspection.Status === 10
                                      }
                                    />
                                    <span>{index + 1}</span>
                                  </div>
                                )
                              })}
                          </div>
                        </Col>
                        <Col xs={12}>
                          <Form.Group>
                            <Form.Label>Αναφέρατε λεπτομέρειες</Form.Label>
                            <Form.Control
                              as="textarea"
                              rows={2}
                              value={inspection.Vehicle.ProtectionMeasures || ""}
                              onChange={e => handleInputChange("Vehicle.ProtectionMeasures", e.target.value)}
                              disabled={
                                isSubmitting.status || inspection.Status === 99 || inspection.Status === 10
                              }
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                </div>

                {/* Additional Equipment */}
                <div className="col-12">
                  <Card>
                    <Card.Header className="bg-navy text-white">
                      <h6 className="mb-0">4. Πρόσθετα Εξαρτήματα</h6>
                    </Card.Header>
                    <Card.Body>
                      <Row className="g-3">
                        {lookupOptions.extras.map(extra => (
                          <Col key={extra.Code} md={6}>
                            <Form.Group>
                              <Form.Label>{extra.Name}</Form.Label>
                              <div className="d-flex gap-3">
                                <Form.Check
                                  inline
                                  type="radio"
                                  label="ΝΑΙ"
                                  name={`extra-${extra.Code}`}
                                  checked={inspection.Vehicle.ExtraEquipmentList?.short?.includes(extra.Code)}
                                  onChange={() =>
                                    handleArrayFieldChange("Vehicle.ExtraEquipmentList", extra.Code)
                                  }
                                  disabled={
                                    isSubmitting.status ||
                                    inspection.Status === 99 ||
                                    inspection.Status === 10
                                  }
                                />
                                <Form.Check
                                  inline
                                  type="radio"
                                  label="ΟΧΙ"
                                  name={`extra-${extra.Code}`}
                                  checked={
                                    !inspection.Vehicle.ExtraEquipmentList?.short?.includes(extra.Code)
                                  }
                                  onChange={() =>
                                    handleArrayFieldChange("Vehicle.ExtraEquipmentList", extra.Code)
                                  }
                                  disabled={
                                    isSubmitting.status ||
                                    inspection.Status === 99 ||
                                    inspection.Status === 10
                                  }
                                />
                              </div>
                            </Form.Group>
                          </Col>
                        ))}
                        <Col xs={12}>
                          <Form.Group>
                            <Form.Label>Άλλο (αναφέρατε):</Form.Label>
                            <Form.Control
                              as="textarea"
                              rows={2}
                              value={inspection.Vehicle.ExtraEquipment || ""}
                              onChange={e => handleInputChange("Vehicle.ExtraEquipment", e.target.value)}
                              disabled={
                                isSubmitting.status || inspection.Status === 99 || inspection.Status === 10
                              }
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                </div>

                {/* Files */}
                <div className="col-12" data-container-query>
                  <Card>
                    <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                      <div className="d-flex align-items-center">
                        <i className="fas fa-file me-2" />
                        <h6 className="mb-0">Αρχεία</h6>
                      </div>

                      <div className="d-flex gap-2">
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => setShowFileUpload(true)}
                          disabled={inspection.Status === 10 || isSubmitting.status}>
                          <i className="fas fa-upload me-2" />
                          Μεταφόρτωση Αρχείου
                        </Button>
                        {inspection.InspectionFilesList?.InspectionFilesDetails?.length > 0 && (
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={handleDownloadAll}
                            disabled={isSubmitting.status}>
                            {isSubmitting.status && isSubmitting.action === "downloadAll" ? (
                              <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                {isSubmitting.progress || "Λήψη Αρχείων..."}
                              </>
                            ) : (
                              <>
                                <i className="fas fa-download me-2" />
                                Λήψη Όλων
                              </>
                            )}
                          </Button>
                        )}
                      </div>
                    </Card.Header>
                    <Card.Body>
                      {inspection.InspectionFilesList?.InspectionFilesDetails?.length ? (
                        <div className="d-flex flex-wrap gap-4 justify-content-center justify-content-container-md-start">
                          {inspection.InspectionFilesList.InspectionFilesDetails.map(file => (
                            <div
                              key={file.FileID}
                              className="position-relative"
                              style={{
                                width: "150px",
                                cursor: "pointer"
                              }}>
                              <div
                                className="file-preview-container border rounded p-2 text-center"
                                onClick={() => handleFileDownload(file)}
                                style={{
                                  height: "150px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  backgroundColor: "#f8f9fa",
                                  transition: "all 0.2s ease-in-out"
                                }}>
                                {isSubmitting.status &&
                                isSubmitting.action === "download" &&
                                !isPreviewableFile(file.FileName) &&
                                downloadingFileId === file.FileID ? (
                                  <div className="d-flex justify-content-center align-items-center h-100">
                                    <div className="spinner-border text-primary" role="status">
                                      <span className="visually-hidden">Φόρτωση...</span>
                                    </div>
                                  </div>
                                ) : file.FileName.toLowerCase().match(/\.(jpg|jpeg|png|gif)$/) ? (
                                  <img
                                    src={`data:image/jpeg;base64,${file.FileByte}`}
                                    alt={file.FileName}
                                    style={{
                                      maxWidth: "100%",
                                      maxHeight: "100%",
                                      objectFit: "contain"
                                    }}
                                  />
                                ) : file.FileName.toLowerCase().endsWith(".pdf") ? (
                                  <i className="fas fa-file-pdf fa-3x text-danger" />
                                ) : file.FileName.toLowerCase().endsWith(".webm") ? (
                                  <i className="fas fa-file-video fa-3x text-primary" />
                                ) : (
                                  <i className="fas fa-file fa-3x text-secondary" />
                                )}
                                <div
                                  className="file-actions position-absolute top-0 end-0 m-2 opacity-0"
                                  style={{ transition: "opacity 0.2s ease-in-out" }}>
                                  <ButtonGroup size="sm">
                                    <Button
                                      variant="light"
                                      className="shadow-sm"
                                      onClick={e => {
                                        e.stopPropagation()
                                        handleFileDownload(file)
                                      }}
                                      disabled={isSubmitting.status}>
                                      <i
                                        className={`fas ${
                                          isPreviewableFile(file.FileName) ? "fa-eye" : "fa-download"
                                        }`}
                                      />
                                    </Button>
                                    <Button
                                      variant="light"
                                      className="shadow-sm"
                                      onClick={e => {
                                        e.stopPropagation()
                                        handleDeleteFile(file)
                                      }}
                                      disabled={
                                        isSubmitting.status ||
                                        inspection.Status === 99 ||
                                        inspection.Status === 10
                                      }>
                                      <i className="fas fa-trash text-danger" />
                                    </Button>
                                  </ButtonGroup>
                                </div>
                              </div>
                              <div className="text-center mt-2">
                                <small className="d-block text-truncate" style={{ maxWidth: "150px" }}>
                                  {file.FileName}
                                </small>
                                <small className="text-muted">
                                  {getAppLookupValue("FilesKind", file.FileKindID)}
                                </small>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-4 text-muted">
                          <i className="fas fa-file fa-3x mb-3" />
                          <div>Δεν υπάρχουν διαθέσιμα αρχεία</div>
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </div>
              </div>
            </>
          ) : (
            <div>Δεν έχει επιλεγεί έλεγχος.</div>
          )}

          {/* Notification Preferences Modal */}
          <Modal
            show={showNotificationModal}
            onHide={() => !dynamic && setShowNotificationModal(false)}
            backdrop={dynamic ? "static" : true}
            keyboard={!dynamic}>
            <Modal.Header closeButton={!dynamic}>
              <Modal.Title>
                <i className="fas fa-bell me-2" />
                Επιλογές Ειδοποίησης
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Form>
                <Form.Group>
                  <Form.Label>Μέθοδος Ειδοποίησης</Form.Label>
                  <div className="notification-methods">
                    <div className="d-flex align-items-center mb-2 gap-2">
                      <Form.Check
                        type="checkbox"
                        label="SMS"
                        checked={notificationPrefs.sms}
                        onChange={e => setNotificationPrefs(prev => ({ ...prev, sms: e.target.checked }))}
                        className="mb-0"
                      />
                      <Form.Control
                        type="text"
                        value={notificationPhone}
                        onChange={e => setNotificationPhone(e.target.value)}
                        placeholder="Εισάγετε τηλέφωνο επικοινωνίας"
                        disabled={!notificationPrefs.sms}
                        className="flex-grow-1"
                      />
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <Form.Check
                        type="checkbox"
                        label="Email"
                        checked={notificationPrefs.email}
                        onChange={e => setNotificationPrefs(prev => ({ ...prev, email: e.target.checked }))}
                        className="mb-0"
                      />
                      <Form.Control
                        type="email"
                        value={notificationEmail}
                        onChange={e => setNotificationEmail(e.target.value)}
                        placeholder="Εισάγετε email επικοινωνίας"
                        disabled={!notificationPrefs.email}
                        className="flex-grow-1"
                      />
                    </div>
                  </div>
                </Form.Group>
              </Form>
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="outline-secondary"
                onClick={() => {
                  setShowNotificationModal(false)
                  if (dynamic) {
                    navigate("/")
                  }
                }}>
                Ακύρωση
              </Button>
              <Button
                variant="primary"
                onClick={handleConfirmEExpert}
                disabled={
                  isModalSubmitting ||
                  (notificationPrefs.sms && !notificationPhone) ||
                  (notificationPrefs.email && !notificationEmail)
                }>
                {isModalSubmitting ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Έναρξη...
                  </>
                ) : (
                  "Έναρξη"
                )}
              </Button>
            </Modal.Footer>
          </Modal>

          {/* Add the container query styles */}
          <style>
            {`
            [data-container-query] {
              container-type: inline-size;
              container-name: responsive-container;
            }

            @container responsive-container (min-width: 768px) {
              .col-container-md-6 {
                width: 50%;
              }
              .col-container-md-3 {
                width: 25%;
              }
              .flex-container-md-row {
                flex-direction: row !important;
              }
              .justify-content-container-md-start {
                justify-content: flex-start !important;
              }
            }

            .bg-navy {
              background-color: #1F4E79 !important;
            }

            .table {
              background-color: transparent;
            }

            /* Ensure table cells and their content are visible */
            .table td {
              padding: 0.5rem;
              vertical-align: middle;
              background-color: transparent !important;
              color: #000000 !important;
            }

            /* Style form controls */
            .form-control, .form-select {
              background-color: #ffffff !important;
              border: 1px solid #dee2e6;
              border-radius: 0.25rem;
              padding: 0.375rem 0.75rem;
              color: #212529 !important;
            }

            .form-control:focus, .form-select:focus {
              background-color: #ffffff !important;
              border-color: #86b7fe;
              box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
            }

            /* Style form labels */
            .form-label {
              color: #000000 !important;
            }

            .form-check-input:checked {
              background-color: #0d6efd;
              border-color: #0d6efd;
            }

            /* Style form controls when disabled */
            .form-control:disabled, .form-select:disabled {
              background-color: #e9ecef !important;
              opacity: 0.65;
            }

            /* Radio button labels */
            .form-check-label {
              color: #000000 !important;
            }

            /* File preview container hover effects */
            .file-preview-container:hover {
              background-color: #e9ecef !important;
            }
            .file-preview-container:hover .file-actions {
              opacity: 1 !important;
            }
          `}
          </style>
        </div>
      )}

      {showFileUpload && (
        <FileUploadModal
          show={showFileUpload}
          onHide={() => setShowFileUpload(false)}
          onUpload={handleFileUpload}
          fileKinds={lookupOptions.fileKind}
        />
      )}
      <PrintInspectionModal
        show={showPrintModal}
        onHide={() => setShowPrintModal(false)}
        isPrinting={isPrinting}
        onPrint={handlePrint}
        printProgress={printProgress}
      />
      <SearchResultsModal />
    </Layout>
  )
}

export default InspectionDetails
